import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { Table, Modal } from "antd";
import { getStaffQuarterRequestsByStatus, getPendingWillingnessRequests, getPendingUnwillingnessRequests, processStaffQuarterUnwillingnessApproval, getStaffQuarterPendingApprovals, getAdminVacationIntimations, getPendingVacationRequests, getAllAllotments, extendStaffQuarterOccupation, getStaffQuarterAdminPermissions, getStaffQuarterFinancePendingApprovals, getMutualExchangesList, getMutualExchangePendingApprovals, approveRejectMutualExchange, getStaffQuarterLocations } from "../../../../core/data/redux/staffQuarterSlice";
import OccupationWithdrawalPendingList from "./OccupationWithdrawalPendingList";
import { toast } from "react-toastify";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";
import { formatFlatNoWithBlock, resolveFlatCategory } from "../staffQuarterUtils";
import AllotmentLetterCustomizerModal from "../components/AllotmentLetterCustomizerModal";
import { canReviewMutualExchange, readMutualExchanges } from "../management/mutualExchangeData";
import { getMutualExchangeEmployeeExchanges } from "../../../../core/data/redux/staffQuarterSlice";
import MutualExchangeHistory from "../components/MutualExchangeHistory";

const extractDataArray = (raw: any): any[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.data?.data)) return raw.data.data;
    if (Array.isArray(raw.data?.requests)) return raw.data.requests;
    if (Array.isArray(raw.data?.list)) return raw.data.list;
    if (Array.isArray(raw.data?.unwillingnessRequests)) return raw.data.unwillingnessRequests;
    if (Array.isArray(raw.requests)) return raw.requests;
    if (Array.isArray(raw.list)) return raw.list;
    if (Array.isArray(raw.result)) return raw.result;
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.unwillingnessRequests)) return raw.unwillingnessRequests;
    if (raw.data && typeof raw.data === "object") return extractDataArray(raw.data);
    return [];
};

const AdminRequests = () => {
    const routes = all_routes;
    const dispatch = useDispatch<any>();
    const location = useLocation();
    const isAllotmentPage = location.pathname.includes("/allotments") || location.pathname.includes("/allotment");

    const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
    const adminAuth = useSelector((state: any) => state.adminAuthState);
    const user = adminAuth?.user?.data?.data?.user || JSON.parse(localStorage.getItem("user") || "{}");
    const userEmpId = user?.empId || "00019195";
    const approverId = String(userEmpId);

    const sqPermissions = staffQuarterState?.adminPermissions?.permissions || [];
    const sqRoles = staffQuarterState?.adminPermissions?.roles || [];
    const isHrAdmin = sqRoles.includes("HR_ADMIN");

    const getPermissionForModule = (moduleName: string) => {
        if (!Array.isArray(sqPermissions) || sqPermissions.length === 0) return true;

        // Find all permissions for this module where the role matches one of user's roles
        const permsForModule = sqPermissions.filter((p: any) => p.module === moduleName && sqRoles.includes(p.role));

        if (permsForModule.length > 0) {
            // If any role explicitly grants access, allow it
            if (permsForModule.some((p: any) => p.canView === true)) {
                return true;
            }
            // If we have rules but none grant access (all are false), deny it
            return false;
        }

        // Default to true if no rules are defined for this module
        return true;
    };

    const [status, setStatus] = useState("PENDING");
    const [activeTab, setActiveTab] = useState(() => isAllotmentPage ? "ALLOTMENT" : "REGISTRATION"); // REGISTRATION, WILLINGNESS, UNWILLINGNESS, OCCUPATION, VACATION, FINANCE, or ALLOTMENT
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (isAllotmentPage) {
            setActiveTab("ALLOTMENT");
        } else if (activeTab === "ALLOTMENT") {
            setActiveTab("REGISTRATION");
        }
    }, [isAllotmentPage]);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    const handleToggleExpand = (recordKey: React.Key) => {
        setExpandedRowKeys(prev => (prev.includes(recordKey) ? [] : [recordKey]));
    };

    const getRowKey = (record: any) => String(record.id || record.registrationId || record.registrationReferenceNumber || record.employeeNumber);

    const [letterModalOpen, setLetterModalOpen] = useState(false);
    const [selectedLetterRecord, setSelectedLetterRecord] = useState<any>(null);

    const [unwillingnessModalOpen, setUnwillingnessModalOpen] = useState(false);
    const [selectedUnwillingnessRecord, setSelectedUnwillingnessRecord] = useState<any>(null);
    const [unwillingnessApproverRemarks, setUnwillingnessApproverRemarks] = useState("");
    const [isProcessingUnwillingness, setIsProcessingUnwillingness] = useState(false);

    const [mutualExchangeModalOpen, setMutualExchangeModalOpen] = useState(false);
    const [selectedMutualExchangeRecord, setSelectedMutualExchangeRecord] = useState<any>(null);
    const [mutualExchangeRemarks, setMutualExchangeRemarks] = useState("");
    const [remarksError, setRemarksError] = useState("");
    const [isProcessingAction, setIsProcessingAction] = useState(false);
    const [exchangeHistory, setExchangeHistory] = useState<any>(null);
    const [exchangeHistoryLoading, setExchangeHistoryLoading] = useState(false);
    const [exchangeHistoryError, setExchangeHistoryError] = useState("");
    const canReviewSelectedExchange = canReviewMutualExchange(selectedMutualExchangeRecord, approverId);

    useEffect(() => {
        if (!mutualExchangeModalOpen || !selectedMutualExchangeRecord) return;
        let cancelled = false;
        const record = selectedMutualExchangeRecord;
        const empId = String(record.emp1Id || record.emp1?.empNo || "").trim();
        setExchangeHistory(null);
        setExchangeHistoryError("");
        if (!empId) {
            setExchangeHistoryLoading(false);
            setExchangeHistoryError("Applicant employee number is unavailable. Cannot load approval history.");
            return;
        }
        setExchangeHistoryLoading(true);
        dispatch(getMutualExchangeEmployeeExchanges({ empId: empId.padStart(8, "0") }))
            .unwrap().then((response: any) => {
                const records = readMutualExchanges(response);
                const requestId = record.mutualExchangeId ?? record.id;
                const reference = record.referenceNumber || record.referenceNo;
                const match = records.find((item: any) => requestId != null && String(item.id) === String(requestId)) ||
                    records.find((item: any) => reference && item.referenceNo === reference);
                if (!match) throw new Error("Approval history was not returned for this request.");
                if (!cancelled) setExchangeHistory(match);
            }).catch((error: any) => {
                if (!cancelled) setExchangeHistoryError(typeof error === "string" ? error : error?.message || "Failed to load approval history.");
            }).finally(() => {
                if (!cancelled) setExchangeHistoryLoading(false);
            });
        return () => { cancelled = true; };
    }, [dispatch, mutualExchangeModalOpen, selectedMutualExchangeRecord]);

    const openMutualExchangeModal = (record: any) => {
        setExchangeHistory(null);
        setExchangeHistoryError("");
        setExchangeHistoryLoading(true);
        setSelectedMutualExchangeRecord(record);
        setMutualExchangeRemarks("");
        setRemarksError("");
        setMutualExchangeModalOpen(true);
    };

    const handleApproveRejectMutualExchange = async (action: "APPROVE" | "REJECT") => {
        if (!canReviewSelectedExchange || isProcessingAction) return;
        if (action === "REJECT" && !mutualExchangeRemarks.trim()) {
            setRemarksError("Remarks are mandatory when rejecting a mutual exchange request.");
            toast.error("Rejection remarks are mandatory. Please enter the reason for rejection.");
            return;
        }
        setRemarksError("");

        if (!selectedMutualExchangeRecord) return;

        setIsProcessingAction(true);
        try {
            const payload: any = {
                id: selectedMutualExchangeRecord.id || selectedMutualExchangeRecord.approvalId,
                approvalId: selectedMutualExchangeRecord.approvalId || selectedMutualExchangeRecord.id,
                mutualExchangeId: selectedMutualExchangeRecord.mutualExchangeId || selectedMutualExchangeRecord.id,
                referenceNumber: selectedMutualExchangeRecord.referenceNumber || selectedMutualExchangeRecord.referenceNo,
                action: action,
                status: action === "APPROVE" ? "APPROVED" : "REJECTED",
                remarks: mutualExchangeRemarks.trim() || undefined,
            };

            const res = await dispatch(approveRejectMutualExchange(payload)).unwrap();
            if (res?.success !== false) {
                toast.success(`Mutual exchange request ${action === "APPROVE" ? "approved" : "rejected"} successfully!`);
                setMutualExchangeModalOpen(false);
                setSelectedMutualExchangeRecord(null);
                setMutualExchangeRemarks("");
                setRemarksError("");

                const targetId = selectedMutualExchangeRecord.id || selectedMutualExchangeRecord.approvalId || selectedMutualExchangeRecord.mutualExchangeId;
                const newStatus = action === "APPROVE" ? "APPROVED" : "REJECTED";

                setMutualExchangeList((prevList) =>
                    prevList.map((item) => {
                        const itemId = item.id || item.approvalId || item.mutualExchangeId;
                        if (
                            (targetId != null && String(itemId) === String(targetId)) ||
                            (item.referenceNo && item.referenceNo === selectedMutualExchangeRecord.referenceNumber) ||
                            (item.referenceNumber && item.referenceNumber === selectedMutualExchangeRecord.referenceNo)
                        ) {
                            return { ...item, status: newStatus, exchangeStatus: newStatus };
                        }
                        return item;
                    })
                );

                try {
                    const saved = localStorage.getItem("dmrc_staff_quarter_mutual_exchanges");
                    if (saved) {
                        const parsed = JSON.parse(saved);
                        if (Array.isArray(parsed)) {
                            const updated = parsed.map((item: any) => {
                                const itemId = item.id || item.approvalId || item.mutualExchangeId;
                                if (
                                    (targetId != null && String(itemId) === String(targetId)) ||
                                    (item.referenceNo && item.referenceNo === selectedMutualExchangeRecord.referenceNumber) ||
                                    (item.referenceNumber && item.referenceNumber === selectedMutualExchangeRecord.referenceNo)
                                ) {
                                    return { ...item, status: newStatus, exchangeStatus: newStatus };
                                }
                                return item;
                            });
                            localStorage.setItem("dmrc_staff_quarter_mutual_exchanges", JSON.stringify(updated));
                        }
                    }
                } catch (e) { }

                setStatus(newStatus);
            } else {
                toast.error(res?.message || `Failed to ${action.toLowerCase()} mutual exchange request.`);
            }
        } catch (err: any) {
            console.error("Approve/reject mutual exchange error:", err);
            toast.error(err || `Failed to ${action.toLowerCase()} mutual exchange request.`);
        } finally {
            setIsProcessingAction(false);
        }
    };

    const openLetterModal = (record: any, w?: any) => {
        const flatStr = formatFlatNoWithBlock(w || record, (w || record)?.block, w || record) || (w || record)?.flatNumber || (w || record)?.flatNo || "";
        const combinedData = {
            ...record,
            ...(w || {}),
            allotmentId: w?.allotmentId || record.allotmentId || record.allotmentDetails?.id || record.id,
            registrationId: record.registrationId || record.id,
            employeeName: record.employeeName || record.employee_name || w?.employeeName,
            employeeNumber: record.employeeNumber || record.empId || record.emp_id || w?.employeeNumber,
            quarterType: w?.quarterType || w?.quarter_type || record.quarterType || record.quarter_type || "II",
            flatNumber: flatStr,
            location: (w || record)?.location || (w || record)?.location_name || (w || record)?.colony_name || "",
            pool: (w || record)?.pool || (w || record)?.poolName || record.pool || "OPN",
            referenceNumber: (w || record)?.referenceNumber || record.registrationReferenceNumber,
        };
        setSelectedLetterRecord(combinedData);
        setLetterModalOpen(true);
    };

    const [extendModalOpen, setExtendModalOpen] = useState(false);
    const [selectedExtendRecord, setSelectedExtendRecord] = useState<any>(null);
    const [extendDate, setExtendDate] = useState<string>("");

    const openExtendModal = (record: any) => {
        setSelectedExtendRecord(record);
        setExtendDate("");
        setExtendModalOpen(true);
    };

    const handleExtendConfirm = async () => {
        if (!extendDate) {
            toast.warning("Please select a future date to extend.");
            return;
        }

        try {
            const payload = {
                registrationId: selectedExtendRecord?.registrationId || selectedExtendRecord?.referenceNumber || selectedExtendRecord?.id,
                extensionDate: extendDate
            };

            const resultAction = await dispatch(extendStaffQuarterOccupation(payload));

            if (extendStaffQuarterOccupation.fulfilled.match(resultAction)) {
                toast.success(`Occupation extended to ${extendDate} successfully!`);
                setExtendModalOpen(false);
                setSelectedExtendRecord(null);

                // Refresh the data if possible, else rely on user to refresh
                // dispatch(getStaffQuarterPendingApprovals()); 
            } else {
                toast.error(resultAction.payload || "Failed to extend occupation.");
            }
        } catch (err: any) {
            toast.error("An error occurred while extending.");
        }
    };
    const formatDateTime = (date: any) => {
        if (!date) return "-";
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;

        const day = String(d.getDate()).padStart(2, "0");
        const month = d.toLocaleString('default', { month: 'short' });
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");

        return (
            <div className="d-flex flex-column">
                <span className="text-dark fw-medium" style={{ fontSize: '13px' }}>
                    <i className="ti ti-calendar-event me-1 text-muted"></i>
                    {`${day} ${month}, ${year}`}
                </span>
                <span className="text-muted extra-small" style={{ fontSize: '11px' }}>
                    <i className="ti ti-clock me-1"></i>
                    {`${hours}:${minutes}`}
                </span>
            </div>
        );
    };

    let requestsData: any[] = [];
    if (Array.isArray(staffQuarterState?.requests)) {
        requestsData = staffQuarterState.requests;
    } else if (Array.isArray(staffQuarterState?.requests?.data)) {
        requestsData = staffQuarterState.requests.data;
    } else if (Array.isArray(staffQuarterState?.requests?.data?.requests)) {
        requestsData = staffQuarterState.requests.data.requests;
    }

    let allAllotmentsData: any[] = [];
    if (Array.isArray(staffQuarterState?.allAllotments)) {
        allAllotmentsData = staffQuarterState.allAllotments;
    } else if (Array.isArray(staffQuarterState?.allAllotments?.data)) {
        allAllotmentsData = staffQuarterState.allAllotments.data;
    } else if (Array.isArray(staffQuarterState?.allAllotments?.data?.requests)) {
        allAllotmentsData = staffQuarterState.allAllotments.data.requests;
    }

    const groupedAllotmentsData = useMemo(() => {
        if (!Array.isArray(allAllotmentsData) || allAllotmentsData.length === 0) return [];

        const groups: { [key: string]: any } = {};

        allAllotmentsData.forEach((item: any) => {
            const groupKey = String(item.registrationId || item.registrationReferenceNumber || item.employeeNumber || item.empId || item.id);

            const flatNoStr = formatFlatNoWithBlock(item, item.block, item) || item.flatNumber || item.flatNo || "";
            const locationStr = item.location || item.location_name || "";
            const willingnessRef = item.referenceNumber || item.willingnessReferenceNumber || item.willingnessRef || "";

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    ...item,
                    id: item.registrationId || item.id,
                    registrationId: item.registrationId || item.id,
                    registrationReferenceNumber: item.registrationReferenceNumber || item.referenceNumber,
                    allotmentDate: item.allotmentDate || item.allottedDate || item.allotment_date,
                    flats: flatNoStr ? [{ flatNo: flatNoStr, location: locationStr, willingnessRef, item }] : [],
                    locations: locationStr ? [locationStr] : [],
                    willingnessList: [item],
                    totalOpportunities: 1,
                };
            } else {
                const existing = groups[groupKey];
                if (!existing.allotmentDate && (item.allotmentDate || item.allottedDate || item.allotment_date)) {
                    existing.allotmentDate = item.allotmentDate || item.allottedDate || item.allotment_date;
                }
                if (flatNoStr && !existing.flats.some((f: any) => f.flatNo === flatNoStr && f.willingnessRef === willingnessRef)) {
                    existing.flats.push({ flatNo: flatNoStr, location: locationStr, willingnessRef, item });
                }
                if (locationStr && !existing.locations.includes(locationStr)) {
                    existing.locations.push(locationStr);
                }
                existing.willingnessList.push(item);
                existing.totalOpportunities += 1;
            }
        });

        return Object.values(groups);
    }, [allAllotmentsData]);

    const filteredGroupedAllotmentsData = useMemo(() => {
        if (!Array.isArray(groupedAllotmentsData) || groupedAllotmentsData.length === 0) return [];
        if (!searchTerm.trim()) return groupedAllotmentsData;
        const term = searchTerm.trim().toLowerCase();
        return groupedAllotmentsData.filter((item: any) => {
            const empNo = String(item.employeeNumber || item.empId || item.emp_id || item.employee_number || "").toLowerCase();
            const empName = String(item.employeeName || item.employee_name || "").toLowerCase();
            const refNo = String(item.registrationReferenceNumber || item.referenceNumber || item.registrationId || "").toLowerCase();
            const designation = String(item.designation || item.designation_name || "").toLowerCase();

            const flatNos = Array.isArray(item.flats)
                ? item.flats.map((f: any) => String(f.flatNo || "")).join(" ").toLowerCase()
                : "";
            const locations = Array.isArray(item.locations)
                ? item.locations.join(" ").toLowerCase()
                : String(item.location || "").toLowerCase();

            return empNo.includes(term) ||
                   empName.includes(term) ||
                   refNo.includes(term) ||
                   designation.includes(term) ||
                   flatNos.includes(term) ||
                   locations.includes(term);
        });
    }, [groupedAllotmentsData, searchTerm]);

    const filteredRequestsData = useMemo(() => {
        if (!Array.isArray(requestsData) || requestsData.length === 0) return [];
        if (!searchTerm.trim()) return requestsData;
        const term = searchTerm.trim().toLowerCase();
        return requestsData.filter((item: any) => {
            const empNo = String(item.employeeNumber || item.empId || item.emp_id || item.employee_number || "").toLowerCase();
            const empName = String(item.employeeName || item.employee_name || "").toLowerCase();
            const refNo = String(item.referenceNumber || item.registrationReferenceNumber || item.registrationId || "").toLowerCase();
            const designation = String(item.designation || item.designation_name || "").toLowerCase();

            return empNo.includes(term) || empName.includes(term) || refNo.includes(term) || designation.includes(term);
        });
    }, [requestsData, searchTerm]);

    const pendingWillingnessData = extractDataArray(staffQuarterState?.pendingWillingness);

    const isLoading = staffQuarterState.loading === "pending";
    const isWillingnessLoading = staffQuarterState.loading === "pending"; // Assuming shared loading state for simplicity or you can use a separate one if added to slice

    const [mutualExchangeList, setMutualExchangeList] = useState<any[]>(() => {
        try {
            const saved = localStorage.getItem("dmrc_staff_quarter_mutual_exchanges");
            if (saved) return JSON.parse(saved);
        } catch (e) { }
        return [];
    });

    const fetchAllData = () => {
        dispatch(getStaffQuarterAdminPermissions({}));
        dispatch(getStaffQuarterLocations({}));

        dispatch(getStaffQuarterRequestsByStatus({
            approverId: String(approverId),
            status: status === "EXPIRED" ? undefined : status
        }));

        dispatch(getPendingWillingnessRequests({
            approverId: String(approverId),
            status: status === "EXPIRED" ? undefined : status
        }));

        dispatch(getPendingUnwillingnessRequests({
            approverId: String(approverId),
            status: status === "EXPIRED" ? undefined : status
        }));

        dispatch(getStaffQuarterPendingApprovals({
            status: status,
            role: (status === "EXPIRED" && isHrAdmin) ? "HR_ADMIN" : undefined
        }));

        dispatch(getPendingVacationRequests({
            status: status === "EXPIRED" ? undefined : status
        }));

        dispatch(getStaffQuarterFinancePendingApprovals({
            approverId: String(approverId),
            status: status === "EXPIRED" ? undefined : status
        }));

        dispatch(getAllAllotments({
            status: status === "ALLOTED" || status === "ALLOTTED" ? "ALLOTTED" : (status === "EXPIRED" ? undefined : status)
        }));

        dispatch(getMutualExchangePendingApprovals({
            approverId: String(approverId),
            status: status === "EXPIRED" ? undefined : status
        })).then((res: any) => {
            const list = res?.payload?.data?.data || res?.payload?.data || (Array.isArray(res?.payload) ? res.payload : []);
            if (Array.isArray(list) && list.length > 0) {
                setMutualExchangeList(list);
            } else {
                dispatch(getMutualExchangesList({})).then((subRes: any) => {
                    const fallbackList = subRes?.payload?.data?.data || subRes?.payload?.data || (Array.isArray(subRes?.payload) ? subRes.payload : []);
                    if (Array.isArray(fallbackList) && fallbackList.length > 0) {
                        setMutualExchangeList(fallbackList);
                    } else {
                        try {
                            const saved = localStorage.getItem("dmrc_staff_quarter_mutual_exchanges");
                            if (saved) setMutualExchangeList(JSON.parse(saved));
                        } catch (e) { }
                    }
                }).catch(() => {
                    try {
                        const saved = localStorage.getItem("dmrc_staff_quarter_mutual_exchanges");
                        if (saved) setMutualExchangeList(JSON.parse(saved));
                    } catch (e) { }
                });
            }
        }).catch(() => {
            try {
                const saved = localStorage.getItem("dmrc_staff_quarter_mutual_exchanges");
                if (saved) setMutualExchangeList(JSON.parse(saved));
            } catch (e) { }
        });
    };

    useEffect(() => {
        setExpandedRowKeys([]);
        fetchAllData();
    }, [dispatch, approverId, status, activeTab]);

    const columns = [
        {
            title: "Reference Number",
            dataIndex: "referenceNumber",
            key: "referenceNumber",
            render: (text: string, record: any) => activeTab === "ALLOTMENT" ? (record.registrationReferenceNumber || text) : text,
            sorter: (a: any, b: any) => {
                const aRef = activeTab === "ALLOTMENT" ? (a.registrationReferenceNumber || a.referenceNumber || "") : (a.referenceNumber || "");
                const bRef = activeTab === "ALLOTMENT" ? (b.registrationReferenceNumber || b.referenceNumber || "") : (b.referenceNumber || "");
                return aRef.localeCompare(bRef);
            },
        },
        {
            title: "Employee",
            dataIndex: "employeeName",
            key: "employeeName",
            render: (text: string, record: any) => {
                const name = text || record.employeeName || "N/A";
                const img = record.image || record.employeeImage;
                const empNum = record.employeeNumber || record.empId;
                const totalReqs = activeTab === "ALLOTMENT" && record.flats?.length > 1 ? record.flats.length : 0;
                return (
                    <div className="d-flex align-items-center">
                        <div className="avatar avatar-md me-2">
                            {img ? (
                                <img
                                    src={img.startsWith("data:") ? img : `data:image/jpeg;base64,${img}`}
                                    className="rounded-circle"
                                    alt="user"
                                    style={{ width: '32px', height: '32px' }}
                                />
                            ) : (
                                <span className="avatar-title rounded-circle bg-primary-light text-primary" style={{ backgroundColor: 'rgba(var(--bs-primary-rgb), 0.1)', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {name?.charAt(0)}
                                </span>
                            )}
                        </div>
                        <div>
                            <div className="d-flex align-items-center gap-1">
                                <h6 className="mb-0" style={{ fontSize: '14px' }}>{name !== "N/A" ? name : empNum}</h6>
                                {totalReqs > 1 && (
                                    <span className="badge bg-primary-light text-primary" style={{ fontSize: '10px', padding: '2px 6px' }}>
                                        {totalReqs} Requests
                                    </span>
                                )}
                            </div>
                            <span className="text-muted small">{empNum}</span>
                        </div>
                    </div>
                );
            },
            sorter: (a: any, b: any) => (a.employeeName || "").localeCompare(b.employeeName || ""),
        },
        {
            title: "Designation",
            dataIndex: "designation",
            key: "designation",
            render: (text: string, record: any) => text || record.employeeDesignation || "N/A",
            sorter: (a: any, b: any) => (a.designation || "").localeCompare(b.designation || ""),
        },
        {
            title: activeTab === "ALLOTMENT" ? "Registration Date" : "Date",
            dataIndex: "date",
            key: "date",
            render: (text: string, record: any) => {
                const displayDate = activeTab === "ALLOTMENT" ? (record.registrationDate || text) : text;
                return formatDateTime(displayDate);
            },
            sorter: (a: any, b: any) => {
                const aDate = activeTab === "ALLOTMENT" ? (a.registrationDate || a.date || "") : (a.date || "");
                const bDate = activeTab === "ALLOTMENT" ? (b.registrationDate || b.date || "") : (b.date || "");
                return aDate.localeCompare(bDate);
            },
        },
        ...(activeTab === "ALLOTMENT" ? [
            {
                title: "Allotment Date",
                dataIndex: "allotmentDate",
                key: "allotmentDate",
                render: (_: string, record: any) => {
                    const allotDate = record.allotmentDate || record.allottedDate || record.allotment_date || record.willingnessList?.find((w: any) => w.allotmentDate || w.allottedDate || w.allotment_date)?.allotmentDate;
                    return formatDateTime(allotDate);
                },
                sorter: (a: any, b: any) => {
                    const aDate = (a.allotmentDate || a.allottedDate || a.allotment_date || "");
                    const bDate = (b.allotmentDate || b.allottedDate || b.allotment_date || "");
                    return aDate.localeCompare(bDate);
                },
            },
            {
                title: "Flat No",
                dataIndex: "flatNumber",
                key: "flatNumber",
                render: (_: string, record: any) => {
                    if (record.flats && record.flats.length > 0) {
                        return (
                            <div className="d-flex flex-wrap gap-1 align-items-center">
                                {record.flats.map((f: any, idx: number) => (
                                    <span
                                        key={idx}
                                        className="badge bg-light text-dark border px-2 py-1"
                                        style={{ fontSize: '12px', fontWeight: 600 }}
                                    >
                                        {f.flatNo || f}
                                    </span>
                                ))}
                            </div>
                        );
                    }
                    const fallbackFlat = formatFlatNoWithBlock(record) || record.flatNumber || record.flatNo || "N/A";
                    return <span className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: '12px', fontWeight: 600 }}>{fallbackFlat}</span>;
                },
                sorter: (a: any, b: any) => {
                    const aFlats = (a.flats?.map((f: any) => f.flatNo || f).join(", ") || formatFlatNoWithBlock(a) || a.flatNumber || "");
                    const bFlats = (b.flats?.map((f: any) => f.flatNo || f).join(", ") || formatFlatNoWithBlock(b) || b.flatNumber || "");
                    return aFlats.localeCompare(bFlats);
                },
            },
            {
                title: "Location",
                dataIndex: "location",
                key: "location",
                render: (text: string, record: any) => {
                    if (record.locations && record.locations.length > 0) {
                        return (
                            <div className="d-flex flex-wrap gap-1 align-items-center">
                                {record.locations.map((loc: string, idx: number) => (
                                    <span key={idx} className="fw-medium text-dark">
                                        {loc}{idx < record.locations.length - 1 ? ", " : ""}
                                    </span>
                                ))}
                            </div>
                        );
                    }
                    return text || record.location || "N/A";
                },
                sorter: (a: any, b: any) => {
                    const aLoc = (a.locations?.join(", ") || a.location || "");
                    const bLoc = (b.locations?.join(", ") || b.location || "");
                    return aLoc.localeCompare(bLoc);
                },
            }
        ] : [
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (text: string, record: any) => {
                    const displayStatus = record.approverStatus || text;
                    return (
                        <span
                            className={`badge ${displayStatus?.toUpperCase() === "APPROVED" || displayStatus?.toUpperCase() === "ALLOTED" || displayStatus?.toUpperCase() === "ALLOTTED"
                                ? "bg-success"
                                : displayStatus?.toUpperCase() === "PENDING"
                                    ? "bg-warning text-dark"
                                    : "bg-danger"
                                }`}
                        >
                            {displayStatus}
                        </span>
                    );
                },
                sorter: (a: any, b: any) => a.status.localeCompare(b.status),
            }
        ]),
        {
            title: "Action",
            key: "action",
            render: (_: any, record: any) => {
                const isAllotment = activeTab === "ALLOTMENT";
                const rowKey = getRowKey(record);
                const isExpanded = expandedRowKeys.includes(rowKey);
                const isAllotted = status === "ALLOTED" || status === "ALLOTTED" || record.status === "ALLOTED" || record.status === "ALLOTTED";

                return (
                    <div className="d-flex gap-2 align-items-center">
                        {isAllotment ? (
                            <button
                                onClick={() => handleToggleExpand(rowKey)}
                                className="btn btn-primary btn-sm"
                            >
                                {isExpanded ? "Collapse" : "View"}
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    const path = activeTab === "VACATION"
                                        ? routes.staffQuarterAdmin.vacationDetails.replace(":id", encodeRouteParam(record.allotmentReferenceNumber || record.referenceNumber || record.registrationReferenceNumber || record.id))
                                        : routes.staffQuarterAdmin.requestDetails.replace(":id", encodeRouteParam(record.registrationId || record.id));
                                    const isWillingnessOrAllotment = activeTab === "WILLINGNESS" || activeTab === "ALLOTMENT";
                                    const query = activeTab === "FINANCE"
                                        ? "?tab=finance"
                                        : activeTab === "OCCUPATION"
                                            ? "?tab=occupation"
                                            : (isWillingnessOrAllotment
                                                ? `?willingnessId=${record.id}&willingnessRef=${record.referenceNumber}${activeTab === "ALLOTMENT" ? "&fromAllotInbox=true" : ""}`
                                                : "");
                                    navigate(`${path}${query}`);
                                }}
                                className="btn btn-primary btn-sm"
                            >
                                View
                            </button>
                        )}
                        {activeTab === "OCCUPATION" && status === "EXPIRED" && (
                            <button
                                onClick={() => openExtendModal(record)}
                                className="btn btn-warning btn-sm"
                            >
                                Extend
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    const mutualExchangeColumns = [
        {
            title: "Reference Number",
            dataIndex: "referenceNumber",
            key: "referenceNumber",
            render: (text: string, record: any) => {
                const ref = record.referenceNumber || record.referenceNo || record.id || text || "N/A";
                const isDiff = (record.exchangeType || "").toUpperCase() === "DIFFERENT_LOCATIONS";
                return (
                    <div>
                        <span className="fw-bold text-dark d-block" style={{ fontSize: '13.5px' }}>{ref}</span>
                        <span className={`badge ${isDiff ? 'bg-purple-subtle text-purple border' : 'bg-info-subtle text-info border'} mt-1`} style={{ fontSize: '11px' }}>
                            {isDiff ? "Different Locations" : "Same Location"}
                        </span>
                    </div>
                );
            },
            sorter: (a: any, b: any) => (a.referenceNumber || a.referenceNo || a.id || "").localeCompare(b.referenceNumber || b.referenceNo || b.id || "")
        },
        {
            title: "Employee 1 (Current Quarter)",
            key: "emp1",
            render: (_: any, record: any) => {
                const name = record.emp1Name || record.emp1?.name || "N/A";
                const empId = record.emp1Id || record.emp1?.empNo || "";
                const desig = record.emp1Designation || record.emp1?.designation || "";
                const dept = record.emp1Department || record.emp1?.department || "";
                const flat = record.emp1FlatNumber || record.emp1?.quarterNo || "";
                const loc = record.emp1Location || record.emp1?.location || "";
                const qType = record.emp1QuarterType || record.emp1?.quarterType || "";
                return (
                    <div>
                        <div className="fw-bold text-dark">{name}</div>
                        <div className="text-muted small">
                            Emp ID: <strong>{empId}</strong> {desig ? `· ${desig}` : ''} {dept ? `(${dept})` : ''}
                        </div>
                        {flat && (
                            <div className="text-danger small mt-1">
                                <i className="ti ti-home me-1"></i>
                                {flat} {loc ? `(${loc}${qType ? ` - ${qType}` : ''})` : ''}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Employee 2 (Current Quarter)",
            key: "emp2",
            render: (_: any, record: any) => {
                const name = record.emp2Name || record.emp2?.name || "N/A";
                const empId = record.emp2Id || record.emp2?.empNo || "";
                const desig = record.emp2Designation || record.emp2?.designation || "";
                const dept = record.emp2Department || record.emp2?.department || "";
                const flat = record.emp2FlatNumber || record.emp2?.quarterNo || "";
                const loc = record.emp2Location || record.emp2?.location || "";
                const qType = record.emp2QuarterType || record.emp2?.quarterType || "";
                return (
                    <div>
                        <div className="fw-bold text-dark">{name}</div>
                        <div className="text-muted small">
                            Emp ID: <strong>{empId}</strong> {desig ? `· ${desig}` : ''} {dept ? `(${dept})` : ''}
                        </div>
                        {flat && (
                            <div className="text-danger small mt-1">
                                <i className="ti ti-home me-1"></i>
                                {flat} {loc ? `(${loc}${qType ? ` - ${qType}` : ''})` : ''}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            render: (text: string, record: any) => formatDateTime(record.date || record.createdAt || text),
            sorter: (a: any, b: any) => (a.date || a.createdAt || "").localeCompare(b.date || b.createdAt || "")
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text: string, record: any) => {
                const st = (record.status || record.exchangeStatus || text || "PENDING").toUpperCase();
                return (
                    <span className={`badge ${st === "APPROVED" || st === "EXECUTED" ? 'bg-success' : st === "PENDING" ? 'bg-warning text-dark' : 'bg-danger'}`}>
                        {st === "EXECUTED" ? "Executed" : st === "APPROVED" ? "Approved" : st === "REJECTED" ? "Rejected" : "Pending Review"}
                    </span>
                );
            }
        },
        {
            title: "Action",
            key: "action",
            render: (_: any, record: any) => {
                return (
                    <div className="d-flex align-items-center gap-2">
                        <button
                            onClick={() => openMutualExchangeModal(record)}
                            className="btn btn-primary btn-sm"
                        >
                            View
                        </button>
                    </div>
                );
            }
        }
    ];

    const getFormattedFlatNo = (rec: any) => {
        if (!rec) return "-";
        const formatted = formatFlatNoWithBlock(
            rec,
            rec.block || rec.blockName || rec.block_name || rec.flatDetails?.block || rec.flat?.block,
            rec
        );
        if (formatted && formatted !== "-") {
            return formatted.startsWith("Flat ") ? formatted.replace(/^Flat\s+/i, "") : formatted;
        }
        const rawFlat = rec.flatNo || rec.flatNumber || rec.flat_no || rec.flat_number || "";
        const rawBlock = rec.block || rec.blockName || rec.block_name || "";
        if (rawBlock && rawFlat) {
            return `${rawBlock}-${rawFlat}`;
        }
        return rawFlat || "-";
    };

    const unwillingnessColumns = [
        {
            title: "Reference Details",
            key: "referenceNumber",
            render: (_: any, record: any) => {
                const regRef = record.registrationReferenceNumber || record.registrationRef || record.referenceNumber || "-";
                const willRef = record.willingnessReferenceNumber || record.willingnessRef;
                const unwillRef = record.unwillingnessReferenceNumber || record.unwillingnessRef;
                return (
                    <div>
                        <span className="fw-bold text-dark d-block" style={{ fontSize: '13.5px' }}>{regRef}</span>
                        {willRef && (
                            <span className="badge bg-success-subtle text-success border border-success-subtle mt-1 me-1" style={{ fontSize: '11px' }}>
                                Willingness: {willRef}
                            </span>
                        )}
                        {unwillRef && (
                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle mt-1" style={{ fontSize: '11px' }}>
                                Unwillingness Ref: {unwillRef}
                            </span>
                        )}
                    </div>
                );
            },
            sorter: (a: any, b: any) => (a.registrationReferenceNumber || a.referenceNumber || "").localeCompare(b.registrationReferenceNumber || b.referenceNumber || "")
        },
        {
            title: "Employee",
            key: "employeeName",
            render: (_: any, record: any) => {
                const name = record.employeeName || "N/A";
                const img = record.image || record.employeeImage;
                const empNum = record.employeeNumber || record.empId;
                return (
                    <div className="d-flex align-items-center">
                        <div className="avatar avatar-md me-2">
                            {img ? (
                                <img
                                    src={img.startsWith("data:") ? img : `data:image/jpeg;base64,${img}`}
                                    className="rounded-circle"
                                    alt="user"
                                    style={{ width: '32px', height: '32px' }}
                                />
                            ) : (
                                <span className="avatar-title rounded-circle bg-danger-subtle text-danger" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
                                    {name?.charAt(0)}
                                </span>
                            )}
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ fontSize: '14px' }}>{name}</h6>
                            <span className="text-muted small">Emp ID: {empNum}</span>
                        </div>
                    </div>
                );
            },
            sorter: (a: any, b: any) => (a.employeeName || "").localeCompare(b.employeeName || "")
        },
        {
            title: "Designation & Dept",
            key: "designation",
            render: (_: any, record: any) => {
                const desig = record.designation || "-";
                const dept = record.department;
                return (
                    <div>
                        <div className="fw-medium text-dark">{desig}</div>
                        {dept && <div className="text-muted small">{dept}</div>}
                    </div>
                );
            },
            sorter: (a: any, b: any) => (a.designation || "").localeCompare(b.designation || "")
        },
        {
            title: "Quarter / Flat",
            key: "quarterInfo",
            render: (_: any, record: any) => {
                const flats = record.flats && record.flats.length > 0
                    ? record.flats
                    : [{ flatNo: getFormattedFlatNo(record), quarterType: record.quarterType || record.quarter_type }];
                return (
                    <div className="d-flex flex-column gap-1">
                        {flats.map((f: any, idx: number) => {
                            const raw = f.flatNo || (f.item ? getFormattedFlatNo(f.item) : "-");
                            const cleanFlat = raw.replace(/^Flat\s+/i, "");
                            return (
                                <div key={idx} className="d-flex align-items-center gap-1">
                                    {cleanFlat && cleanFlat !== "-" && cleanFlat !== "N/A" ? (
                                        <span className="badge bg-light text-dark border px-2 py-1 mb-0 d-inline-block" style={{ fontSize: '12px', fontWeight: 600 }}>
                                            Flat {cleanFlat}
                                        </span>
                                    ) : null}
                                    {f.quarterType && <span className="text-muted small">Type: {f.quarterType}</span>}
                                </div>
                            );
                        })}
                    </div>
                );
            }
        },
        {
            title: "Location",
            dataIndex: "locationPreference",
            key: "locationPreference",
            render: (text: string, record: any) => {
                const loc = record.locationName || text || record.location || record.location_name || "-";
                return (
                    <div className="d-flex align-items-center gap-1">
                        <i className="ti ti-map-pin text-danger" style={{ fontSize: '14px' }}></i>
                        <span className="fw-medium text-dark">{loc}</span>
                    </div>
                );
            },
            sorter: (a: any, b: any) => (a.locationName || a.locationPreference || "").localeCompare(b.locationName || b.locationPreference || "")
        },
        {
            title: "Priority",
            key: "priority",
            render: (_: any, record: any) => {
                const pNo = record.priorityNo || record.priorityNumber || record.priorityRank;
                return pNo != null ? (
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle fw-bold" style={{ fontSize: '12px' }}>
                        #{pNo}
                    </span>
                ) : (
                    <span className="text-muted small">-</span>
                );
            }
        },
        {
            title: "Reason / Remarks",
            key: "userRemarks",
            render: (_: any, record: any) => {
                const remarks = record.unwillingnessRemarks || record.userRemarks || record.remarks || record.reason || record.unwillingnessDetails?.remarks || "-";
                return (
                    <div style={{ maxWidth: '220px' }}>
                        <span className="text-muted small" title={remarks}>
                            {remarks}
                        </span>
                    </div>
                );
            }
        },
        {
            title: "Request Date",
            dataIndex: "requestDate",
            key: "requestDate",
            render: (text: string, record: any) => {
                const date = record.unwillingnessDate || record.debarmentDate || text || record.requestDate || record.createdAt || record.date;
                return date ? formatDateTime(date) : "-";
            },
            sorter: (a: any, b: any) => (a.unwillingnessDate || a.requestDate || a.createdAt || "").localeCompare(b.unwillingnessDate || b.requestDate || b.createdAt || "")
        },
        {
            title: "Status",
            key: "status",
            render: (_: any, record: any) => {
                const st = (record.unwillingnessStatus || record.unwillingnessDetails?.status || record.status || "PENDING").toUpperCase();
                return (
                    <span
                        className={`badge ${
                            st === "APPROVED" || st === "ACCEPTED"
                                ? "bg-success"
                                : st.includes("PENDING")
                                ? "bg-warning text-dark"
                                : "bg-danger"
                        }`}
                    >
                        {st === "UNWILLINGNESS_PENDING" ? "Unwillingness Pending" : st}
                    </span>
                );
            }
        },
        {
            title: "Action",
            key: "action",
            render: (_: any, record: any) => {
                return (
                    <div className="d-flex gap-2 align-items-center">
                        <button
                            onClick={() => {
                                setSelectedUnwillingnessRecord(record);
                                setUnwillingnessApproverRemarks("");
                                setUnwillingnessModalOpen(true);
                            }}
                            className="btn btn-primary btn-sm"
                        >
                            View
                        </button>
                    </div>
                );
            }
        }
    ];
    const locationsList = Array.isArray(staffQuarterState?.locations)
        ? staffQuarterState.locations
        : (Array.isArray(staffQuarterState?.locations?.data)
            ? staffQuarterState.locations.data
            : []);

    const resolveNumericLocationId = (item: any, locNameFallback?: string): number => {
        if (!item) return 1;

        // Check direct candidate properties on item
        const candList = [
            item.locationId,
            item.location_id,
            item.locationCode,
            item.locationPreferenceId,
            item.locationDetails?.id,
            item.locationDetails?.locationId,
            item.locationObj?.id,
            item.locationObj?.locationId,
            item.flatDetails?.locationId,
            item.flatDetails?.location_id,
            item.flat?.locationId,
            item.flat?.location_id
        ];

        for (const cand of candList) {
            if (cand != null && cand !== "" && !isNaN(Number(cand))) {
                return Number(cand);
            }
        }

        // Check nested willingnessDetails, flats, or willingnessItems
        const nested = item.willingnessDetails || item.flats || item.willingnessItems || item.willingnessRequests;
        if (Array.isArray(nested) && nested.length > 0) {
            for (const sub of nested) {
                const subTarget = sub.item || sub;
                const subCand = [
                    subTarget.locationId,
                    subTarget.location_id,
                    subTarget.locationCode,
                    subTarget.locationPreferenceId,
                    subTarget.locationDetails?.id,
                    subTarget.locationDetails?.locationId,
                    subTarget.flatDetails?.locationId
                ];
                for (const sc of subCand) {
                    if (sc != null && sc !== "" && !isNaN(Number(sc))) {
                        return Number(sc);
                    }
                }
            }
        }

        // Match locationName / locNameFallback against master locationsList
        const locName = String(
            locNameFallback ||
            (typeof item.location === "string" ? item.location : null) ||
            item.locationName ||
            item.location_name ||
            item.locationPreference ||
            ""
        ).trim();

        if (locName && Array.isArray(locationsList) && locationsList.length > 0) {
            const found = locationsList.find((l: any) =>
                String(l.locationName || l.name || l.location || "").trim().toLowerCase() === locName.toLowerCase() ||
                String(l.code || l.locationCode || "").trim().toLowerCase() === locName.toLowerCase()
            );
            if (found) {
                const foundId = found.id || found.locationId || found.location_id;
                if (foundId != null && !isNaN(Number(foundId))) {
                    return Number(foundId);
                }
            }
        }

        if (item.locationId && !isNaN(Number(item.locationId))) {
            return Number(item.locationId);
        }

        return 1;
    };

    const handleProcessUnwillingness = (action: "APPROVE" | "REJECT") => {
        if (!selectedUnwillingnessRecord) return;
        setIsProcessingUnwillingness(true);

        const currentApproverId = String(approverId || "00019195");

        const locationId = resolveNumericLocationId(selectedUnwillingnessRecord, selectedUnwillingnessRecord.locationName || selectedUnwillingnessRecord.locationPreference);

        let rawWillingnessIds: any[] = [];
        if (Array.isArray(selectedUnwillingnessRecord.willingnessIds) && selectedUnwillingnessRecord.willingnessIds.length > 0) {
            rawWillingnessIds = selectedUnwillingnessRecord.willingnessIds;
        } else if (Array.isArray(selectedUnwillingnessRecord.flats) && selectedUnwillingnessRecord.flats.length > 0) {
            rawWillingnessIds = selectedUnwillingnessRecord.flats
                .map((f: any) => f.willingnessId || f.item?.willingnessId || f.item?.id || f.id)
                .filter(Boolean);
        }

        if (rawWillingnessIds.length === 0) {
            if (selectedUnwillingnessRecord.willingnessId) rawWillingnessIds.push(selectedUnwillingnessRecord.willingnessId);
            else if (selectedUnwillingnessRecord.id) rawWillingnessIds.push(selectedUnwillingnessRecord.id);
        }

        const willingnessIds = Array.from(new Set(rawWillingnessIds.map((id: any) => (isNaN(Number(id)) ? id : Number(id)))));

        const payload = {
            locationId: Number(locationId),
            willingnessIds: willingnessIds,
            action: action,
            remarks: unwillingnessApproverRemarks.trim() || (
                action === "APPROVE"
                    ? `Approved unwillingness for location ${locationId} willingness IDs`
                    : `Unwillingness rejected for location ${locationId}`
            ),
            approverId: currentApproverId
        };

        dispatch(processStaffQuarterUnwillingnessApproval(payload))
            .then((res: any) => {
                setIsProcessingUnwillingness(false);
                if (res.payload?.success || res.payload?.status === 200 || res.payload?.statusCode === 200 || res.payload?.data?.success) {
                    toast.success(res.payload?.message || res.payload?.data?.message || `Unwillingness request ${action === "APPROVE" ? "approved" : "rejected"} successfully.`);
                    setUnwillingnessModalOpen(false);
                    setSelectedUnwillingnessRecord(null);
                    setUnwillingnessApproverRemarks("");
                    fetchAllData();
                } else {
                    toast.error(res.payload?.message || res.payload?.data?.message || `Failed to ${action.toLowerCase()} unwillingness request.`);
                }
            })
            .catch(() => {
                setIsProcessingUnwillingness(false);
                toast.error(`An error occurred while processing unwillingness request.`);
            });
    };

    const navigate = useNavigate();

    const pendingApprovalsObj = staffQuarterState?.pendingApprovals;
    const pendingData = Array.isArray(pendingApprovalsObj) ? pendingApprovalsObj : (pendingApprovalsObj?.data || []);
    const vacationDataRaw = staffQuarterState?.pendingVacations?.data || [];
    const vacationData = Array.isArray(vacationDataRaw) ? vacationDataRaw : (vacationDataRaw ? [vacationDataRaw] : []);

    const pendingUnwillingnessDataRaw = staffQuarterState?.pendingUnwillingnessRequests;
    
    const pendingUnwillingnessData = extractDataArray(pendingUnwillingnessDataRaw);

    const groupedUnwillingnessData = useMemo(() => {
        if (!Array.isArray(pendingUnwillingnessData) || pendingUnwillingnessData.length === 0) return [];

        const getSubRegId = (sub: any) => {
            if (!sub || typeof sub !== "object") return null;
            const val = sub.registrationId ?? sub.registration_id ?? sub.registrationRef ?? sub.registrationReferenceNumber ?? sub.registration?.id ?? sub.registration?.registrationId ?? sub.registrationDetails?.id ?? sub.registrationDetails?.registrationId;
            return val != null ? String(val).trim() : null;
        };

        const getSubEmpId = (sub: any) => {
            if (!sub || typeof sub !== "object") return null;
            const val = sub.employeeNumber ?? sub.employee_number ?? sub.employeeId ?? sub.employee_id ?? sub.empId ?? sub.emp_id ?? sub.empNo ?? sub.emp_no ?? sub.employee?.empId ?? sub.employee?.employeeNumber ?? sub.employeeDetails?.empId ?? sub.employeeDetails?.employeeNumber;
            return val != null ? String(val).trim() : null;
        };

        const groups: { [key: string]: any } = {};

        pendingUnwillingnessData.forEach((item: any) => {
            const reg = item.registration || item.registrationDetails || item.employeeDetails || item;
            const emp = item.employee || item.employeeDetails || reg || item;

            const empId = String(item.employeeNumber || item.empId || item.emp_id || item.employeeId || item.employee_id || item.empNo || emp.employeeNumber || emp.empId || emp.emp_id || emp.employeeId || reg.employeeNumber || reg.empId || "EMP").trim();
            const regId = String(item.registrationId || item.registration_id || item.registrationRef || item.registrationReferenceNumber || reg.id || reg.registrationId || reg.registration_id || empId).trim();
            
            // Extract location ID & name for this item
            const numericLocId = resolveNumericLocationId(item);
            const locId = String(numericLocId);
            const locObj = item.locationDetails || item.locationObj || (typeof item.location === "object" ? item.location : null);
            const locName = typeof item.location === "string" ? item.location : (item.locationName || item.location_name || item.locationPreference || locObj?.locationName || locObj?.name || `Location ${locId}`);

            const empName = item.employeeName || item.employee_name || item.empName || emp.employeeName || emp.name || reg.employeeName || "N/A";
            const desig = item.designation || item.empDesignation || emp.designation || reg.designation || "-";
            const dept = item.department || item.empDepartment || emp.department || reg.department || "";
            const priorityNo = item.priorityNo ?? item.priorityNumber ?? item.priorityRank ?? reg.priorityNo ?? reg.priorityRank;

            const regRef = item.registrationReferenceNumber || item.registrationRef || item.referenceNumber || reg.referenceNumber || reg.registrationReferenceNumber || "-";
            const userRemarks = item.userRemarks || item.remarks || item.unwillingnessRemarks || item.reason || "-";
            const requestDate = item.requestDate || item.createdAt || item.date || item.unwillingnessDate || item.created_at;
            const st = (item.status || item.unwillingnessStatus || item.willingnessStatus || "PENDING").toUpperCase();

            // Group key MUST be unique per registration ID, employee ID AND location ID
            const groupMatchKey = `${regId}_${empId}_loc_${locId}`;

            // Helper to get flat string for a single flat item without rootData bleed
            const getSingleFlatStr = (fItem: any) => {
                if (!fItem) return "";
                const rawFlat = fItem.flatNumber || fItem.flatNo || fItem.flat_number || fItem.flat_no || fItem.flatCode || (fItem.flatDetails ? (fItem.flatDetails.flatNumber || fItem.flatDetails.flatNo) : "") || (fItem.flat ? (fItem.flat.flatNumber || fItem.flat.flatNo) : "");
                const rawBlock = fItem.block || fItem.blockName || fItem.block_name || fItem.flatDetails?.block || fItem.flat?.block || "";
                if (rawBlock && rawFlat) {
                    const cleanFlat = String(rawFlat).trim();
                    const cleanBlock = String(rawBlock).trim();
                    if (cleanFlat.toLowerCase().startsWith(cleanBlock.toLowerCase() + "-")) return cleanFlat;
                    return `${cleanBlock}-${cleanFlat}`;
                }
                return rawFlat ? String(rawFlat).trim() : "";
            };

            // Extract flat details from item or item.willingnessDetails / item.flats matching this employee registration AND location
            const collectFlatsForItem = (parentItem: any): { flatNo: string; quarterType: string; willingnessId: any; item: any }[] => {
                const collected: { flatNo: string; quarterType: string; willingnessId: any; item: any }[] = [];
                
                // If parentItem has willingnessDetails/flats array
                const nestedList = parentItem.willingnessDetails || parentItem.flats || parentItem.willingnessRequests || parentItem.willingnessList;
                if (Array.isArray(nestedList) && nestedList.length > 0) {
                    nestedList.forEach((sub: any) => {
                        const subRegId = getSubRegId(sub);
                        const subEmpId = getSubEmpId(sub);

                        const subLocId = sub.locationId || sub.location_id || sub.locationCode || (sub.locationDetails?.id) || (typeof sub.location === "number" ? String(sub.location) : null);
                        const subLocName = typeof sub.location === "string" ? sub.location : (sub.locationName || sub.location_name || sub.locationPreference || sub.locationDetails?.locationName || sub.locationDetails?.name);

                        // Strict registration check: if subRegId is present, it must match regId or empId
                        const regMatches = !subRegId || subRegId === regId || subRegId === empId;

                        // Strict employee check: if subEmpId is present, it must match empId or regId
                        const empMatches = !subEmpId || subEmpId === empId || subEmpId === regId;

                        // Strict location check: subLocId or subLocName must match locId or locName
                        const locMatches = (!subLocId && !subLocName) ||
                                           (locId && subLocId && String(subLocId) === String(locId)) ||
                                           (locName && subLocName && String(subLocName).trim().toLowerCase() === String(locName).trim().toLowerCase());

                        if (regMatches && empMatches && locMatches) {
                            const fStr = getSingleFlatStr(sub);
                            const subWId = sub.willingnessId || sub.willingness_id || sub.id;
                            const subQType = sub.quarterType || sub.quarter_type || sub.flatDetails?.quarterType || parentItem.quarterType || "";
                            if (fStr) {
                                collected.push({ flatNo: fStr, quarterType: subQType, willingnessId: subWId, item: sub });
                            }
                        }
                    });
                }
                
                // If no nested array yielded matching flats, try parentItem itself
                if (collected.length === 0) {
                    const parentSubRegId = getSubRegId(parentItem);
                    const parentSubEmpId = getSubEmpId(parentItem);
                    const pRegMatches = !parentSubRegId || parentSubRegId === regId || parentSubRegId === empId;
                    const pEmpMatches = !parentSubEmpId || parentSubEmpId === empId || parentSubEmpId === regId;

                    if (pRegMatches && pEmpMatches) {
                        const fStr = getSingleFlatStr(parentItem);
                        const subWId = parentItem.willingnessId || parentItem.willingness_id || parentItem.id;
                        const subQType = parentItem.quarterType || parentItem.quarter_type || parentItem.flatDetails?.quarterType || "";
                        if (fStr) {
                            collected.push({ flatNo: fStr, quarterType: subQType, willingnessId: subWId, item: parentItem });
                        }
                    }
                }
                return collected;
            };

            const itemFlats = collectFlatsForItem(item);
            const itemWillingnessIds = itemFlats.map(f => f.willingnessId).filter(Boolean);

            if (!groups[groupMatchKey]) {
                groups[groupMatchKey] = {
                    ...item,
                    id: item.approvalId || item.id || groupMatchKey,
                    registrationId: regId,
                    registrationReferenceNumber: regRef,
                    employeeName: empName,
                    employeeNumber: empId,
                    designation: desig,
                    department: dept,
                    priorityNo: priorityNo,
                    locationId: numericLocId,
                    locationName: locName,
                    locationPreference: locName,
                    userRemarks: userRemarks,
                    remarks: userRemarks,
                    requestDate: requestDate,
                    status: st,
                    willingnessStatus: item.willingnessStatus || "APPROVED",
                    willingnessIds: itemWillingnessIds.length > 0 ? itemWillingnessIds : (item.willingnessId ? [item.willingnessId] : [item.id]),
                    flats: itemFlats,
                    willingnessItems: [item],
                };
            } else {
                const existing = groups[groupMatchKey];
                itemWillingnessIds.forEach(wId => {
                    if (wId && !existing.willingnessIds.includes(wId)) {
                        existing.willingnessIds.push(wId);
                    }
                });
                itemFlats.forEach(f => {
                    if (f.flatNo && !existing.flats.some((ef: any) => ef.flatNo === f.flatNo)) {
                        existing.flats.push(f);
                    }
                });
                existing.willingnessItems.push(item);
            }
        });

        return Object.values(groups);
    }, [pendingUnwillingnessData]);

    const filteredUnwillingnessData = useMemo(() => {
        if (!Array.isArray(groupedUnwillingnessData) || groupedUnwillingnessData.length === 0) return [];
        let filtered = groupedUnwillingnessData.filter((item: any) => {
            const s = (item.status || item.unwillingnessStatus || item.willingnessStatus || "PENDING").toUpperCase();
            if (status === "PENDING") {
                return s === "PENDING" ||
                       s.includes("PENDING") ||
                       s.includes("UNWILLING") ||
                       s === "SUBMITTED" ||
                       s === "REQUESTED" ||
                       s === "OPEN" ||
                       s === "INITIATED" ||
                       !item.status;
            }
            if (status === "APPROVED") {
                return s === "APPROVED" || s === "ACCEPTED" || s.includes("APPROV");
            }
            if (status === "REJECTED") {
                return s === "REJECTED" || s === "DECLINED" || s.includes("REJECT");
            }
            return s === status.toUpperCase();
        });
        const baseResult = (filtered.length > 0 || status !== "PENDING") ? filtered : groupedUnwillingnessData;
        if (!searchTerm.trim()) return baseResult;
        const term = searchTerm.trim().toLowerCase();
        return baseResult.filter((item: any) => {
            const empNo = String(item.employeeNumber || item.empId || item.emp_id || "").toLowerCase();
            const empName = String(item.employeeName || item.employee_name || "").toLowerCase();
            const refNo = String(item.referenceNumber || item.registrationReferenceNumber || item.willingnessReferenceNumber || "").toLowerCase();
            return empNo.includes(term) || empName.includes(term) || refNo.includes(term);
        });
    }, [groupedUnwillingnessData, status, searchTerm]);

    const filteredWillingnessData = useMemo(() => {
        if (!Array.isArray(pendingWillingnessData) || pendingWillingnessData.length === 0) return [];
        let list = pendingWillingnessData.filter((item: any) => {
            const itemStatus = String(item.status || "").toUpperCase();
            const willingnessStatus = String(item.willingnessStatus || "").toUpperCase();
            const combinedStatus = itemStatus || willingnessStatus || "PENDING";

            if (status === "PENDING") {
                return itemStatus === "PENDING" ||
                       itemStatus === "SUBMITTED" ||
                       itemStatus === "CONFIRMED" ||
                       willingnessStatus === "CONFIRMED" ||
                       willingnessStatus === "PENDING" ||
                       (combinedStatus !== "APPROVED" && combinedStatus !== "REJECTED" && combinedStatus !== "EXPIRED");
            }
            if (status === "APPROVED" || status === "ALLOTED" || status === "ALLOTTED") {
                return itemStatus === "APPROVED" || itemStatus === "ALLOTTED" || itemStatus === "ALLOTED" || willingnessStatus === "APPROVED";
            }
            if (status === "REJECTED") {
                return itemStatus === "REJECTED" || itemStatus === "DECLINED" || willingnessStatus === "REJECTED";
            }
            return combinedStatus === status.toUpperCase();
        });
        if (!searchTerm.trim()) return list;
        const term = searchTerm.trim().toLowerCase();
        return list.filter((item: any) => {
            const empNo = String(item.employeeNumber || item.empId || item.emp_id || "").toLowerCase();
            const empName = String(item.employeeName || item.employee_name || "").toLowerCase();
            const refNo = String(item.referenceNumber || item.willingnessReferenceNumber || "").toLowerCase();
            return empNo.includes(term) || empName.includes(term) || refNo.includes(term);
        });
    }, [pendingWillingnessData, status, searchTerm]);

    const filteredVacationData = vacationData.filter((item: any) => {
        const s = item.status || "PENDING";
        const matchesStatus = s.toUpperCase() === status;
        if (!matchesStatus) return false;
        if (!searchTerm.trim()) return true;
        const term = searchTerm.trim().toLowerCase();
        const empNo = String(item.employeeNumber || item.empId || "").toLowerCase();
        const empName = String(item.employeeName || "").toLowerCase();
        const refNo = String(item.registrationReferenceNumber || item.referenceNumber || "").toLowerCase();
        return empNo.includes(term) || empName.includes(term) || refNo.includes(term);
    });

    const filteredMutualExchangeData = mutualExchangeList.filter((item: any) => {
        const s = item.status || item.exchangeStatus || "PENDING";
        const matchesStatus = s.toUpperCase() === status;
        if (!matchesStatus) return false;
        if (!searchTerm.trim()) return true;
        const term = searchTerm.trim().toLowerCase();
        const emp1No = String(item.emp1Id || item.emp1?.empNo || "").toLowerCase();
        const emp1Name = String(item.emp1Name || item.emp1?.employeeName || "").toLowerCase();
        const emp2No = String(item.emp2Id || item.emp2?.empNo || "").toLowerCase();
        const emp2Name = String(item.emp2Name || item.emp2?.employeeName || "").toLowerCase();
        const refNo = String(item.referenceNumber || item.referenceNo || "").toLowerCase();
        return emp1No.includes(term) || emp1Name.includes(term) || emp2No.includes(term) || emp2Name.includes(term) || refNo.includes(term);
    });

    const financeDataRaw = staffQuarterState?.financePendingApprovals;
    let financeData = [];
    if (Array.isArray(financeDataRaw)) {
        financeData = financeDataRaw;
    } else if (Array.isArray(financeDataRaw?.data)) {
        financeData = financeDataRaw.data;
    } else if (Array.isArray(financeDataRaw?.data?.requests)) {
        financeData = financeDataRaw.data.requests;
    }

    const filteredFinanceData = financeData.filter((item: any) => {
        const s = item.status || item.approverStatus || "PENDING";
        const matchesStatus = s.toUpperCase() === status;
        if (!matchesStatus) return false;
        if (!searchTerm.trim()) return true;
        const term = searchTerm.trim().toLowerCase();
        const reg = item.financeRequest?.registrationDetails || item.registrationDetails || item.registration || item;
        const empNo = String(item.financeRequest?.empId || reg?.empId || reg?.employeeNumber || item.empId || item.employeeNumber || "").toLowerCase();
        const empName = String(reg?.employeeName || item.employeeName || item.employee_name || "").toLowerCase();
        const refNo = String(reg?.referenceNumber || item.referenceNumber || item.registrationReferenceNumber || "").toLowerCase();
        return empNo.includes(term) || empName.includes(term) || refNo.includes(term);
    });

    const occupationCount = Array.isArray(pendingData) ? pendingData.filter((i: any) => i.occupationid || i.type === 'OCCUPATION').length : 0;
    const withdrawalCount = Array.isArray(pendingData) ? pendingData.filter((i: any) => i.withdrawalid || i.type === 'WITHDRAWAL').length : 0;
    const vacationCount = vacationData.length;
    const financeCount = financeData.length;
    const mutualExchangeCount = mutualExchangeList.filter((m: any) => (m.status || m.exchangeStatus || "PENDING").toUpperCase() === "PENDING").length;

    const registrationPendingCount = status === "PENDING" ? requestsData.length : 0;
    const willingnessCount = pendingWillingnessData.length;
    const unwillingnessCount = groupedUnwillingnessData.filter((item: any) => {
        const s = (item.status || item.willingnessStatus || "PENDING").toUpperCase();
        return s === "PENDING" || s === "UNWILLINGNESS_PENDING" || s === "UNWILLING_PENDING";
    }).length;

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title fw-bold text-dark mb-1">
                                {isAllotmentPage ? "Staff Quarter Allotments" : "Staff Quarter Requests"}
                            </h3>
                            <ul className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={routes.staffQuarterAdmin.dashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">
                                    {isAllotmentPage ? "Allotment" : "Requests"}
                                </li>
                            </ul>
                        </div>
                        <div className="col-auto">
                            <button
                                className="btn btn-outline-primary d-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-xs"
                                onClick={fetchAllData}
                                disabled={isLoading}
                                style={{ fontWeight: 600 }}
                            >
                                <i className={`ti ti-refresh ${isLoading ? 'fa-spin' : ''}`} style={{ fontSize: '16px' }} />
                                <span>Refresh</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Modules & Status Toolbar */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                    {/* Primary Tab Navigation (Hidden when on dedicated Allotment page) */}
                    {!isAllotmentPage && (
                        <div className="border-bottom px-3 pt-2">
                            <ul className="nav nav-tabs border-0 custom-clean-main-tabs d-flex flex-nowrap w-100 mb-0" style={{ gap: '4px' }}>
                                {getPermissionForModule("registration") && (
                                    <li className="nav-item flex-fill text-center">
                                        <button
                                            className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-2 ${activeTab === "REGISTRATION" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("REGISTRATION"); setStatus("PENDING"); }}
                                        >
                                            <i className="ti ti-file-text" style={{ fontSize: '18px' }} />
                                            <span>Registration</span>
                                            {status === "PENDING" && requestsData.length > 0 && (
                                                <span className="badge rounded-pill bg-danger ms-1">
                                                    {requestsData.length}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )}

                                {getPermissionForModule("willingness") && (
                                    <li className="nav-item flex-fill text-center">
                                        <button
                                            className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-2 ${activeTab === "WILLINGNESS" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("WILLINGNESS"); setStatus("PENDING"); }}
                                        >
                                            <i className="ti ti-clipboard-check" style={{ fontSize: '18px' }} />
                                            <span>Willingness</span>
                                            {willingnessCount > 0 && (
                                                <span className="badge rounded-pill bg-danger ms-1">
                                                    {willingnessCount}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )}

                                {getPermissionForModule("unwillingness") && (
                                    <li className="nav-item flex-fill text-center">
                                        <button
                                            className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-2 ${activeTab === "UNWILLINGNESS" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("UNWILLINGNESS"); setStatus("PENDING"); }}
                                        >
                                            <i className="ti ti-ban" style={{ fontSize: '18px' }} />
                                            <span>Unwillingness</span>
                                            {unwillingnessCount > 0 && (
                                                <span className="badge rounded-pill bg-danger ms-1">
                                                    {unwillingnessCount}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )}

                                <li className="nav-item flex-fill text-center">
                                    <Link
                                        to={routes.staffQuarter.priorityList}
                                        className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-2 ${activeTab === "MY_PRIORITY" ? "active" : ""}`}
                                    >
                                        <i className="ti ti-list-numbered" style={{ fontSize: '18px' }} />
                                        <span>My Priority</span>
                                    </Link>
                                </li>

                                {getPermissionForModule("occupation") && (
                                    <li className="nav-item flex-fill text-center">
                                        <button
                                            className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-2 ${activeTab === "OCCUPATION" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("OCCUPATION"); setStatus("PENDING"); }}
                                        >
                                            <i className="ti ti-key" style={{ fontSize: '18px' }} />
                                            <span>Occupation</span>
                                            {occupationCount > 0 && (
                                                <span className="badge rounded-pill bg-danger ms-1">
                                                    {occupationCount}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )}

                                {getPermissionForModule("vacation") && (
                                    <li className="nav-item flex-fill text-center">
                                        <button
                                            className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-2 ${activeTab === "VACATION" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("VACATION"); setStatus("PENDING"); }}
                                        >
                                            <i className="ti ti-door-exit" style={{ fontSize: '18px' }} />
                                            <span>Request Vacation</span>
                                            {vacationCount > 0 && (
                                                <span className="badge rounded-pill bg-danger ms-1">
                                                    {vacationCount}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )}

                                {getPermissionForModule("finance") && (
                                    <li className="nav-item flex-fill text-center">
                                        <button
                                            className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-2 ${activeTab === "FINANCE" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("FINANCE"); setStatus("PENDING"); }}
                                        >
                                            <i className="ti ti-receipt-tax" style={{ fontSize: '18px' }} />
                                            <span>Finance</span>
                                            {financeCount > 0 && (
                                                <span className="badge rounded-pill bg-danger ms-1">
                                                    {financeCount}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )}

                                {getPermissionForModule("mutual_exchange") && (
                                    <li className="nav-item flex-fill text-center">
                                        <button
                                            className={`nav-link w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-2 ${activeTab === "MUTUAL_EXCHANGE" ? "active" : ""}`}
                                            onClick={() => { setActiveTab("MUTUAL_EXCHANGE"); setStatus("PENDING"); }}
                                        >
                                            <i className="ti ti-arrows-exchange" style={{ fontSize: '18px' }} />
                                            <span>Mutual Exchange</span>
                                            {mutualExchangeCount > 0 && (
                                                <span className="badge rounded-pill bg-danger ms-1">
                                                    {mutualExchangeCount}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    {/* Status Sub-Filters & Search Bar */}
                    <div className="card-body bg-light p-3">
                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="text-secondary small fw-semibold me-2">Status Filter:</span>
                                {(activeTab === "ALLOTMENT" ? ["PENDING", "ALLOTED"] : activeTab === "OCCUPATION" ? (isHrAdmin ? ["PENDING", "APPROVED", "REJECTED", "EXPIRED"] : ["PENDING", "APPROVED", "REJECTED"]) : ["PENDING", "APPROVED", "REJECTED"]).map((s) => {
                                    const isActive = status === s;
                                    let btnClass = "btn-light text-secondary border";

                                    if (isActive) {
                                        if (s === "PENDING") btnClass = "btn-warning text-dark fw-bold border-warning shadow-xs";
                                        else if (s === "APPROVED" || s === "ALLOTED") btnClass = "btn-success text-white fw-bold border-success shadow-xs";
                                        else if (s === "REJECTED" || s === "EXPIRED") btnClass = "btn-danger text-white fw-bold border-danger shadow-xs";
                                    }

                                    return (
                                        <button
                                            key={s}
                                            className={`btn btn-sm px-3 py-1-5 rounded-pill transition-all ${btnClass}`}
                                            style={{ fontSize: '13px' }}
                                            onClick={() => setStatus(s)}
                                        >
                                            <span style={{ textTransform: 'capitalize' }}>{s.toLowerCase()}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Employee No / Search Input */}
                            <div className="d-flex align-items-center gap-2 ms-auto" style={{ minWidth: '280px' }}>
                                <div className="position-relative w-100">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm ps-4 pe-4 bg-white"
                                        placeholder="Search by Employee No / Name / Ref..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: '13px', height: '34px', borderRadius: '6px' }}
                                    />
                                    <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" style={{ fontSize: '15px' }}></i>
                                    {searchTerm && (
                                        <button
                                            type="button"
                                            className="btn-close position-absolute top-50 end-0 translate-middle-y me-2"
                                            style={{ fontSize: '10px' }}
                                            onClick={() => setSearchTerm('')}
                                            aria-label="Clear search"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <style>{`
                        .custom-clean-main-tabs::-webkit-scrollbar {
                            display: none;
                        }
                        .custom-clean-main-tabs .nav-link {
                            color: #64748b;
                            background: transparent;
                            border: none !important;
                            border-bottom: 3px solid transparent !important;
                            border-radius: 0 !important;
                            font-weight: 600;
                            font-size: 14px;
                            white-space: nowrap;
                            transition: all 0.2s ease;
                        }
                        .custom-clean-main-tabs .nav-link:hover {
                            color: #ff8b00;
                            border-bottom-color: rgba(255, 139, 0, 0.4) !important;
                        }
                        .custom-clean-main-tabs .nav-link.active {
                            background: transparent !important;
                            color: #ff8b00 !important;
                            border-bottom: 3px solid #ff8b00 !important;
                        }
                    `}</style>
                </div>

                {activeTab === "REGISTRATION" || activeTab === "ALLOTMENT" ? (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <Table
                                    className="table-striped"
                                    style={{ overflowX: "auto" }}
                                    columns={columns}
                                    dataSource={activeTab === "ALLOTMENT" ? filteredGroupedAllotmentsData : filteredRequestsData}
                                    rowKey={getRowKey}
                                    loading={isLoading}
                                    expandable={activeTab === "ALLOTMENT" ? {
                                        expandedRowKeys: expandedRowKeys,
                                        onExpand: (expanded, record) => {
                                            const key = getRowKey(record);
                                            setExpandedRowKeys(expanded ? [key] : []);
                                        },
                                        expandedRowRender: (record: any) => {
                                            const nestedWillingness = record.willingnessList || [];
                                            return (
                                                <div className="p-3 bg-light rounded-3 my-2 border">
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>
                                                            <i className="ti ti-home-star me-1 text-primary"></i>
                                                            Willingness Opportunities for {record.employeeName || "Employee"} ({nestedWillingness.length} requests)
                                                        </span>
                                                    </div>
                                                    <Table
                                                        size="small"
                                                        pagination={false}
                                                        rowKey={(w: any) => w.id || w.referenceNumber}
                                                        dataSource={nestedWillingness}
                                                        columns={[
                                                            {
                                                                title: "Willingness Ref",
                                                                dataIndex: "referenceNumber",
                                                                key: "referenceNumber",
                                                                render: (text: string, w: any) => (
                                                                    <span className="fw-bold text-primary">
                                                                        {w.referenceNumber || w.willingnessReferenceNumber || text || "-"}
                                                                    </span>
                                                                )
                                                            },
                                                            {
                                                                title: "Flat No",
                                                                dataIndex: "flatNumber",
                                                                key: "flatNumber",
                                                                render: (_: any, w: any) => (
                                                                    <span className="badge bg-white text-dark border px-2 py-1 fw-semibold">
                                                                        {formatFlatNoWithBlock(w, w.block, w) || w.flatNumber || w.flatNo || "-"}
                                                                    </span>
                                                                )
                                                            },
                                                            {
                                                                title: "Location",
                                                                dataIndex: "location",
                                                                key: "location",
                                                                render: (text: string, w: any) => (
                                                                    <span className="text-secondary fw-medium">
                                                                        {w.location || w.location_name || text || "-"}
                                                                    </span>
                                                                )
                                                            },
                                                            {
                                                                title: "Quarter Type",
                                                                dataIndex: "quarterType",
                                                                key: "quarterType",
                                                                render: (text: string, w: any) => w.quarterType || w.quarter_type || text || "-"
                                                            },
                                                            {
                                                                title: "Category",
                                                                dataIndex: "category",
                                                                key: "category",
                                                                render: (_: any, w: any) => (
                                                                    <span className="badge bg-light text-dark border px-2 py-1 fw-semibold">
                                                                        {resolveFlatCategory(w, w.block, record)}
                                                                    </span>
                                                                )
                                                            },
                                                            {
                                                                title: "Allotment Date",
                                                                dataIndex: "allotmentDate",
                                                                key: "allotmentDate",
                                                                render: (_: any, w: any) => {
                                                                    const allotDate = w.allotmentDate || w.allottedDate || w.allotment_date;
                                                                    return formatDateTime(allotDate);
                                                                }
                                                            },
                                                            {
                                                                title: "Status",
                                                                dataIndex: "willingnessStatus",
                                                                key: "willingnessStatus",
                                                                render: (_: any, w: any) => {
                                                                    const st = (w.willingnessStatus || w.status || "PENDING").toUpperCase();
                                                                    const isSuccess = st === "APPROVED" || st === "ALLOTTED" || st === "ALLOTED" || st === "CONFIRMED";
                                                                    return (
                                                                        <span className={`badge ${isSuccess ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                                            {w.willingnessStatus || w.status || "PENDING"}
                                                                        </span>
                                                                    );
                                                                }
                                                            },
                                                            {
                                                                title: "Action",
                                                                key: "action",
                                                                render: (_: any, w: any) => {
                                                                    const st = (w.willingnessStatus || w.status || w.allotmentStatus || "").toString().toUpperCase();
                                                                    const isItemAllotted = st === "ALLOTTED" || st === "ALLOTED" || st === "FLAT_ALLOTTED" || st === "OCCUPIED" || w.isAllotted === true || w.isAllotted === "true" || w.is_allotted === true || w.is_allotted === "true" || status === "ALLOTED" || status === "ALLOTTED" || !!w.allotmentId;

                                                                    return (
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <button
                                                                                onClick={() => {
                                                                                    if (isItemAllotted) return;
                                                                                    const path = routes.staffQuarterAdmin.requestDetails.replace(":id", encodeRouteParam(record.registrationId || record.id));
                                                                                    const query = `?willingnessId=${w.id}&willingnessRef=${w.referenceNumber}&fromAllotInbox=true`;
                                                                                    navigate(`${path}${query}`);
                                                                                }}
                                                                                disabled={isItemAllotted}
                                                                                className={`btn btn-sm px-3 shadow-xs ${isItemAllotted ? 'btn-secondary disabled opacity-75' : 'btn-primary'}`}
                                                                                style={{ fontSize: '12px', cursor: isItemAllotted ? 'not-allowed' : 'pointer' }}
                                                                            >
                                                                                <i className="ti ti-home-check me-1"></i> Allot Flat
                                                                            </button>
                                                                        </div>
                                                                    );
                                                                }
                                                            }
                                                        ]}
                                                    />
                                                </div>
                                            );
                                        },
                                        rowExpandable: (record: any) => Array.isArray(record.willingnessList) && record.willingnessList.length > 0,
                                    } : undefined}
                                    pagination={{
                                        total: activeTab === "ALLOTMENT" ? filteredGroupedAllotmentsData.length : filteredRequestsData.length,
                                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                                        showSizeChanger: true,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ) : activeTab === "WILLINGNESS" ? (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <Table
                                    className="table-striped"
                                    style={{ overflowX: "auto" }}
                                    columns={columns}
                                    dataSource={filteredWillingnessData.map((item: any) => ({
                                        ...item,
                                        status: item.willingnessStatus || item.status || "PENDING"
                                    }))}
                                    rowKey={(record) => record.id}
                                    loading={isWillingnessLoading}
                                    pagination={{
                                        total: filteredWillingnessData.length,
                                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                                        showSizeChanger: true,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ) : activeTab === "UNWILLINGNESS" ? (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <Table
                                    className="table-striped"
                                    style={{ overflowX: "auto" }}
                                    columns={unwillingnessColumns}
                                    dataSource={filteredUnwillingnessData.map((item: any) => ({
                                        ...item,
                                        id: item.approvalId || item.willingnessId || item.id,
                                        status: item.willingnessStatus || item.status || "PENDING"
                                    }))}
                                    rowKey={(record) => record.approvalId || record.willingnessId || record.id || record.willingnessReferenceNumber || String(Math.random())}
                                    loading={isLoading}
                                    pagination={{
                                        total: filteredUnwillingnessData.length,
                                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                                        showSizeChanger: true,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ) : activeTab === "OCCUPATION" ? (
                    <OccupationWithdrawalPendingList type="OCCUPATION" status={status} onExtendClick={openExtendModal} />
                ) : activeTab === "WITHDRAWAL" ? (
                    <OccupationWithdrawalPendingList type="WITHDRAWAL" status={status} />
                ) : activeTab === "FINANCE" ? (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <Table
                                    className="table-striped"
                                    style={{ overflowX: "auto" }}
                                    columns={columns}
                                    dataSource={filteredFinanceData.map((item: any) => {
                                        const reg = item.financeRequest?.registrationDetails || item.registrationDetails || item.registration || item;
                                        const empId = item.financeRequest?.empId || reg?.empId || reg?.employeeNumber || item.empId || item.employeeNumber;
                                        return {
                                            ...item,
                                            id: item.id || item.financeId,
                                            registrationId: reg?.id || item.registrationId || item.id,
                                            referenceNumber: reg?.referenceNumber || item.referenceNumber || item.registrationReferenceNumber || String(item.id),
                                            employeeName: reg?.employeeName || item.employeeName || item.employee_name || "N/A",
                                            employeeNumber: empId,
                                            designation: reg?.designation || reg?.payScale || item.designation || "N/A",
                                            date: item.createdAt || item.date || item.updatedAt,
                                            status: item.status || item.approverStatus || "PENDING"
                                        };
                                    })}
                                    rowKey={(record) => record.id || record.referenceNumber}
                                    loading={isLoading}
                                    pagination={{
                                        total: filteredFinanceData.length,
                                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                                        showSizeChanger: true,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ) : activeTab === "MUTUAL_EXCHANGE" ? (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <Table
                                    className="table-striped"
                                    style={{ overflowX: "auto" }}
                                    columns={mutualExchangeColumns}
                                    dataSource={filteredMutualExchangeData}
                                    rowKey={(record) => record.id || record.referenceNo || String(Math.random())}
                                    loading={isLoading}
                                    pagination={{
                                        total: filteredMutualExchangeData.length,
                                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                                        showSizeChanger: true,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="card border-0 shadow-sm mt-3">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <Table
                                    className="table-striped"
                                    columns={columns}
                                    dataSource={filteredVacationData.map((v: any) => ({
                                        ...v,
                                        id: v.id,
                                        registrationId: v.registration?.id || v.registrationId,
                                        referenceNumber: v.registrationReferenceNumber || v.registration?.referenceNumber,
                                        employeeName: v.registration?.employeeName || "N/A",
                                        employeeNumber: v.empId || v.registration?.employeeNumber,
                                        date: v.createdAt,
                                        status: v.status
                                    }))}
                                    rowKey={(record) => record.id}
                                    loading={isLoading}
                                    pagination={{
                                        showSizeChanger: true,
                                        showTotal: (total) => `Total ${total} requests`,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Modal
                title={
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '42px', height: '42px', backgroundColor: '#FEF2F2', color: '#E30613', borderRadius: '10px', flexShrink: 0 }}
                        >
                            <i className="ti ti-calendar-event" style={{ fontSize: '22px' }}></i>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '18px' }}>Update Occupation Expiry</h5>
                            <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                                Extend the target date so the employee can complete this occupation.
                            </p>
                        </div>
                    </div>
                }
                open={extendModalOpen}
                onCancel={() => setExtendModalOpen(false)}
                footer={null}
                centered
                closeIcon={<i className="ti ti-x text-dark" style={{ fontSize: '20px' }}></i>}
                width={700}
                zIndex={9999}
                bodyStyle={{ padding: '24px 32px' }}
            >
                <div className="mt-4">
                    {/* Top Info Row */}
                    <div className="row mb-4">
                        <div className="col-md-7">
                            <div className="p-3 d-flex align-items-center gap-3 h-100" style={{ backgroundColor: '#F3F4F6', borderRadius: '12px' }}>
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle"
                                    style={{ width: '40px', height: '40px', border: '1px solid #FCA5A5', color: '#E30613', backgroundColor: '#FEF2F2', flexShrink: 0 }}
                                >
                                    <i className="ti ti-user" style={{ fontSize: '20px' }}></i>
                                </div>
                                <div>
                                    <h6 className="mb-0 fw-bold text-dark text-uppercase" style={{ fontSize: '14px', letterSpacing: '0.3px' }}>
                                        {selectedExtendRecord?.employeeName || "UNKNOWN EMPLOYEE"}
                                    </h6>
                                    <span className="text-muted text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                        {selectedExtendRecord?.employeeNumber || "N/A"} • {selectedExtendRecord?.designation || "N/A"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-5">
                            <div className="p-3 d-flex flex-column justify-content-center h-100" style={{ backgroundColor: '#FEF2F2', color: '#E30613', borderRadius: '12px', border: '1px dashed #FCA5A5' }}>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <i className="ti ti-alert-circle" style={{ fontSize: '18px' }}></i>
                                    <span className="fw-bold" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Occupation Lapsed</span>
                                </div>
                                <span className="fw-medium text-dark ms-4" style={{ fontSize: '14px' }}>
                                    {selectedExtendRecord?.occupationDate ? new Date(selectedExtendRecord.occupationDate).toLocaleDateString('en-GB').replace(/\//g, '-') : "-"}
                                </span>
                            </div>
                        </div>
                    </div>

                    <p className="text-dark fw-medium mb-3" style={{ fontSize: '14px' }}>
                        Pick a new target date. The employee will be informed and the occupation will move back to Pending.
                    </p>

                    {/* Date Picker Input */}
                    <div className="mb-2">
                        <input
                            type="date"
                            className="form-control text-dark fw-bold"
                            style={{
                                borderColor: '#E5E7EB',
                                borderRadius: '8px',
                                height: '50px',
                                fontSize: '15px',
                                width: '100%',
                                backgroundColor: '#F9FAFB'
                            }}
                            min={new Date().toISOString().split('T')[0]}
                            max={new Date(new Date().setDate(new Date().getDate() + 90)).toISOString().split('T')[0]}
                            value={extendDate}
                            onChange={(e) => setExtendDate(e.target.value)}
                        />
                    </div>

                    {/* Info text */}
                    <div className="d-flex gap-2 mb-4" style={{ color: '#6B7280', fontSize: '12px' }}>
                        <i className="ti ti-info-circle mt-1" style={{ fontSize: '16px' }}></i>
                        <span>
                            You can pick any date between {new Date().toLocaleDateString('en-GB').replace(/\//g, '-')} and {new Date(new Date().setDate(new Date().getDate() + 90)).toLocaleDateString('en-GB').replace(/\//g, '-')}. The lapsed date and dates beyond 90 days are disabled.
                        </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                        <button
                            className="btn px-4 fw-bold border-0"
                            style={{ backgroundColor: '#F3F4F6', color: '#374151', borderRadius: '8px', height: '44px', fontSize: '14px' }}
                            onClick={() => setExtendModalOpen(false)}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn px-4 fw-bold border-0"
                            style={{ backgroundColor: '#E30613', color: '#fff', borderRadius: '8px', height: '44px', fontSize: '14px' }}
                            onClick={handleExtendConfirm}
                        >
                            Confirm Extension
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Staff Quarters Allotment Order Customizer & PDF Download Modal */}
            <AllotmentLetterCustomizerModal
                open={letterModalOpen}
                onClose={() => {
                    setLetterModalOpen(false);
                    setSelectedLetterRecord(null);
                }}
                allotmentData={selectedLetterRecord}
            />

            {/* Mutual Exchange Details (Annexure-IV) View Modal */}
            <Modal
                title={
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '44px', height: '44px', backgroundColor: '#FEF2F2', color: '#E30613', borderRadius: '12px', flexShrink: 0 }}
                        >
                            <i className="ti ti-arrows-exchange" style={{ fontSize: '24px' }}></i>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '18px' }}>Mutual Exchange Request Details</h5>
                            <p className="text-muted mb-0" style={{ fontSize: '12.5px' }}>
                                Annexure-IV: Format for Mutual Exchange of Staff Quarters
                            </p>
                        </div>
                    </div>
                }
                open={mutualExchangeModalOpen}
                onCancel={() => {
                    setMutualExchangeModalOpen(false);
                    setSelectedMutualExchangeRecord(null);
                }}
                footer={null}
                centered
                closeIcon={<i className="ti ti-x text-dark" style={{ fontSize: '20px' }}></i>}
                width={860}
                zIndex={9999}
                bodyStyle={{ padding: '20px 28px' }}
            >
                {selectedMutualExchangeRecord && (
                    <div className="mt-2">
                        {/* Top Metadata Header Banner */}
                        <div
                            className="p-3 mb-4 rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-3"
                            style={{
                                background: 'linear-gradient(135deg, #fff5f5 0%, #f8fafc 100%)',
                                border: '1px solid #fed7aa',
                                borderLeft: '4px solid #E30613'
                            }}
                        >
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <span className="fw-bold text-dark fs-6">
                                        {selectedMutualExchangeRecord.referenceNumber || selectedMutualExchangeRecord.referenceNo || selectedMutualExchangeRecord.id || "N/A"}
                                    </span>
                                    <span
                                        className={`badge ${(selectedMutualExchangeRecord.exchangeType || "").toUpperCase() === "DIFFERENT_LOCATIONS"
                                                ? "bg-purple-subtle text-purple border border-purple-subtle"
                                                : "bg-info-subtle text-info border border-info-subtle"
                                            } px-2 py-1`}
                                        style={{ fontSize: "11px" }}
                                    >
                                        {(selectedMutualExchangeRecord.exchangeType || "").toUpperCase() === "DIFFERENT_LOCATIONS"
                                            ? "Different Locations"
                                            : "Same Location"}
                                    </span>
                                </div>
                                <span className="text-muted small">
                                    <i className="ti ti-calendar me-1"></i>
                                    Submitted on: {formatDateTime(selectedMutualExchangeRecord.date || selectedMutualExchangeRecord.createdAt)}
                                </span>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                {selectedMutualExchangeRecord.approvedByRole && (
                                    <span className="badge bg-secondary-subtle text-secondary border px-2 py-1" style={{ fontSize: '11px' }}>
                                        Role: {selectedMutualExchangeRecord.approvedByRole}
                                    </span>
                                )}
                                {(() => {
                                    const st = (selectedMutualExchangeRecord.status || selectedMutualExchangeRecord.exchangeStatus || "PENDING").toUpperCase();
                                    return (
                                        <span className={`badge ${st === "APPROVED" || st === "EXECUTED" ? 'bg-success' : st === "PENDING" ? 'bg-warning text-dark' : 'bg-danger'} px-3 py-2 fs-6`}>
                                            {st === "EXECUTED" ? "Executed" : st === "APPROVED" ? "Approved" : st === "REJECTED" ? "Rejected" : "Pending Review"}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Side-by-Side Employee Particulars */}
                        <div className="row g-3 mb-4">
                            {/* Employee 1 Card */}
                            <div className="col-md-6 col-12">
                                <div className="card h-100 border shadow-none" style={{ borderRadius: '12px', borderColor: '#fca5a5' }}>
                                    <div
                                        className="card-header py-2 px-3 border-bottom d-flex align-items-center justify-content-between"
                                        style={{ backgroundColor: '#fff5f5' }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <div
                                                className="rounded-circle bg-danger text-white d-flex align-items-center justify-content-center fw-bold"
                                                style={{ width: '24px', height: '24px', fontSize: '12px' }}
                                            >
                                                1
                                            </div>
                                            <span className="fw-bold text-danger small text-uppercase">Employee 1 Details</span>
                                        </div>
                                        <span className="badge bg-danger-subtle text-danger fw-bold">
                                            {selectedMutualExchangeRecord.emp1Id || selectedMutualExchangeRecord.emp1?.empNo || "ID"}
                                        </span>
                                    </div>

                                    <div className="card-body p-3">
                                        <h6 className="fw-bold text-dark mb-1">
                                            {selectedMutualExchangeRecord.emp1Name || selectedMutualExchangeRecord.emp1?.name || "N/A"}
                                        </h6>
                                        <p className="text-muted small mb-3">
                                            {selectedMutualExchangeRecord.emp1Designation || selectedMutualExchangeRecord.emp1?.designation || "Staff Officer"}
                                            {selectedMutualExchangeRecord.emp1Department || selectedMutualExchangeRecord.emp1?.department ? ` • ${selectedMutualExchangeRecord.emp1Department || selectedMutualExchangeRecord.emp1?.department}` : ''}
                                        </p>

                                        <div className="p-2 rounded bg-light border mb-2 small">
                                            <div className="d-flex justify-content-between mb-1">
                                                <span className="text-muted">Current Quarter:</span>
                                                <strong className="text-danger">
                                                    {selectedMutualExchangeRecord.emp1FlatNumber || selectedMutualExchangeRecord.emp1?.quarterNo || "N/A"}
                                                </strong>
                                            </div>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span className="text-muted">Type / Pool:</span>
                                                <span className="text-dark fw-medium">
                                                    {selectedMutualExchangeRecord.emp1QuarterType || selectedMutualExchangeRecord.emp1?.quarterType || "Type II"} ({selectedMutualExchangeRecord.emp1Pool || selectedMutualExchangeRecord.emp1?.pool || "Operational Pool"})
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span className="text-muted">Location:</span>
                                                <span className="text-dark fw-medium">
                                                    {selectedMutualExchangeRecord.emp1Location || selectedMutualExchangeRecord.emp1?.location || "N/A"}
                                                </span>
                                            </div>
                                        </div>

                                        {(selectedMutualExchangeRecord.controllingOfficerEmp1 || selectedMutualExchangeRecord.emp1?.controllingOfficerEmp) && (
                                            <div className="extra-small text-muted pt-1">
                                                <span className="fw-semibold text-dark">Controlling Officer:</span> {selectedMutualExchangeRecord.controllingOfficerEmp1 || selectedMutualExchangeRecord.emp1?.controllingOfficerEmp}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Employee 2 Card */}
                            <div className="col-md-6 col-12">
                                <div className="card h-100 border shadow-none" style={{ borderRadius: '12px', borderColor: '#bfdbfe' }}>
                                    <div
                                        className="card-header py-2 px-3 border-bottom d-flex align-items-center justify-content-between"
                                        style={{ backgroundColor: '#eff6ff' }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <div
                                                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                                                style={{ width: '24px', height: '24px', fontSize: '12px' }}
                                            >
                                                2
                                            </div>
                                            <span className="fw-bold text-primary small text-uppercase">Employee 2 Details</span>
                                        </div>
                                        <span className="badge bg-primary-subtle text-primary fw-bold">
                                            {selectedMutualExchangeRecord.emp2Id || selectedMutualExchangeRecord.emp2?.empNo || "ID"}
                                        </span>
                                    </div>

                                    <div className="card-body p-3">
                                        <h6 className="fw-bold text-dark mb-1">
                                            {selectedMutualExchangeRecord.emp2Name || selectedMutualExchangeRecord.emp2?.name || "N/A"}
                                        </h6>
                                        <p className="text-muted small mb-3">
                                            {selectedMutualExchangeRecord.emp2Designation || selectedMutualExchangeRecord.emp2?.designation || "Staff Officer"}
                                            {selectedMutualExchangeRecord.emp2Department || selectedMutualExchangeRecord.emp2?.department ? ` • ${selectedMutualExchangeRecord.emp2Department || selectedMutualExchangeRecord.emp2?.department}` : ''}
                                        </p>

                                        <div className="p-2 rounded bg-light border mb-2 small">
                                            <div className="d-flex justify-content-between mb-1">
                                                <span className="text-muted">Current Quarter:</span>
                                                <strong className="text-primary">
                                                    {selectedMutualExchangeRecord.emp2FlatNumber || selectedMutualExchangeRecord.emp2?.quarterNo || "N/A"}
                                                </strong>
                                            </div>
                                            <div className="d-flex justify-content-between mb-1">
                                                <span className="text-muted">Type / Pool:</span>
                                                <span className="text-dark fw-medium">
                                                    {selectedMutualExchangeRecord.emp2QuarterType || selectedMutualExchangeRecord.emp2?.quarterType || "Type II"} ({selectedMutualExchangeRecord.emp2Pool || selectedMutualExchangeRecord.emp2?.pool || "Operational Pool"})
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span className="text-muted">Location:</span>
                                                <span className="text-dark fw-medium">
                                                    {selectedMutualExchangeRecord.emp2Location || selectedMutualExchangeRecord.emp2?.location || "N/A"}
                                                </span>
                                            </div>
                                        </div>

                                        {(selectedMutualExchangeRecord.controllingOfficerEmp2 || selectedMutualExchangeRecord.emp2?.controllingOfficerEmp) && (
                                            <div className="extra-small text-muted pt-1">
                                                <span className="fw-semibold text-dark">Controlling Officer:</span> {selectedMutualExchangeRecord.controllingOfficerEmp2 || selectedMutualExchangeRecord.emp2?.controllingOfficerEmp}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Proposed Accommodation Swap Outcome */}
                        <div className="card border shadow-none bg-light-subtle rounded-3 mb-3 p-3">
                            <span className="fw-bold text-dark small mb-2 d-block">
                                <i className="ti ti-arrows-transfer-up text-danger me-1"></i> Proposed Mutual Accommodation Swap
                            </span>
                            <div className="row g-2 text-center align-items-center">
                                <div className="col-5">
                                    <div className="p-2 bg-white rounded border shadow-xs">
                                        <span className="text-muted extra-small d-block text-uppercase">Employee 1 Moves To</span>
                                        <strong className="text-primary small d-block">
                                            {selectedMutualExchangeRecord.emp2FlatNumber || selectedMutualExchangeRecord.emp2?.quarterNo || "Emp 2's Flat"}
                                        </strong>
                                        <span className="text-secondary extra-small">
                                            {selectedMutualExchangeRecord.emp2Location || selectedMutualExchangeRecord.emp2?.location || ""}
                                        </span>
                                    </div>
                                </div>
                                <div className="col-2 d-flex justify-content-center">
                                    <div
                                        className="rounded-circle bg-danger-subtle text-danger d-flex align-items-center justify-content-center shadow-xs"
                                        style={{ width: '36px', height: '36px' }}
                                    >
                                        <i className="ti ti-arrows-exchange fs-5"></i>
                                    </div>
                                </div>
                                <div className="col-5">
                                    <div className="p-2 bg-white rounded border shadow-xs">
                                        <span className="text-muted extra-small d-block text-uppercase">Employee 2 Moves To</span>
                                        <strong className="text-danger small d-block">
                                            {selectedMutualExchangeRecord.emp1FlatNumber || selectedMutualExchangeRecord.emp1?.quarterNo || "Emp 1's Flat"}
                                        </strong>
                                        <span className="text-secondary extra-small">
                                            {selectedMutualExchangeRecord.emp1Location || selectedMutualExchangeRecord.emp1?.location || ""}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {exchangeHistoryLoading ? (
                            <div className="border rounded-3 p-3 mb-3 text-muted" role="status">Loading approval history...</div>
                        ) : exchangeHistoryError ? (
                            <div className="alert alert-danger mb-3" role="alert">{exchangeHistoryError}</div>
                        ) : exchangeHistory && <MutualExchangeHistory record={exchangeHistory} />}

                        {/* Remarks & Undertaking */}
                        <div className="p-3 bg-light rounded-3 border mb-3 small">
                            <div className="d-flex align-items-center gap-2 mb-2 text-success fw-semibold">
                                <i className="ti ti-circle-check fs-5"></i>
                                <span>Joint Declaration accepted under Annexure-IV terms & conditions.</span>
                            </div>
                            <div>
                                <span className="text-muted fw-bold d-block">Reason / Grounds:</span>
                                <span className="text-dark">
                                    {selectedMutualExchangeRecord.reason || selectedMutualExchangeRecord.remarks || "Mutual exchange application submitted for accommodation swap per policy."}
                                </span>
                            </div>
                        </div>

                        {/* Approver Remarks for Action */}
                        {(() => {
                            return canReviewSelectedExchange ? (
                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-dark mb-1">
                                        Review Remarks <span className="text-danger fw-semibold">(Mandatory for rejection)</span>
                                    </label>
                                    <textarea
                                        className={`form-control form-control-sm ${remarksError ? 'border-danger' : ''}`}
                                        rows={2}
                                        placeholder="Enter your observations or comments (required when rejecting)..."
                                        value={mutualExchangeRemarks}
                                        onChange={(e) => {
                                            setMutualExchangeRemarks(e.target.value);
                                            if (remarksError) setRemarksError("");
                                        }}
                                        disabled={isProcessingAction}
                                    />
                                    {remarksError && (
                                        <div className="text-danger small mt-1 d-flex align-items-center gap-1" style={{ fontSize: '11.5px' }}>
                                            <i className="ti ti-alert-circle"></i>
                                            <span>{remarksError}</span>
                                        </div>
                                    )}
                                </div>
                            ) : null;
                        })()}

                        {/* Action Buttons */}
                        <div className="d-flex align-items-center justify-content-between pt-3 border-top gap-2">
                            <button
                                className="btn btn-light px-4"
                                onClick={() => {
                                    setMutualExchangeModalOpen(false);
                                    setSelectedMutualExchangeRecord(null);
                                    setMutualExchangeRemarks("");
                                }}
                                disabled={isProcessingAction}
                            >
                                Close
                            </button>

                            {(() => {
                                return canReviewSelectedExchange ? (
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            className="btn btn-danger d-flex align-items-center gap-2 px-3 fw-semibold shadow-xs"
                                            onClick={() => handleApproveRejectMutualExchange("REJECT")}
                                            disabled={isProcessingAction}
                                        >
                                            <i className="ti ti-x"></i>
                                            <span>Reject</span>
                                        </button>

                                        <button
                                            className="btn btn-success d-flex align-items-center gap-2 px-3 fw-semibold shadow-xs"
                                            onClick={() => handleApproveRejectMutualExchange("APPROVE")}
                                            disabled={isProcessingAction}
                                        >
                                            <i className="ti ti-check"></i>
                                            <span>Approve</span>
                                        </button>
                                    </div>
                                ) : (
                                    <span className="badge bg-secondary-subtle text-secondary px-3 py-2">
                                        Status: {(selectedMutualExchangeRecord.status || selectedMutualExchangeRecord.exchangeStatus || "COMPLETED").toUpperCase()}
                                    </span>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal to View Willingness Details & Approve/Reject Unwillingness */}
            <Modal
                title={
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '42px', height: '42px', backgroundColor: '#FEF2F2', color: '#E30613', borderRadius: '10px', flexShrink: 0 }}
                        >
                            <i className="ti ti-file-certificate" style={{ fontSize: '22px' }}></i>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '18px' }}>Unwillingness Request & Review Details</h5>
                            <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                                Review employee unwillingness declaration details and action request.
                            </p>
                        </div>
                    </div>
                }
                open={unwillingnessModalOpen}
                onCancel={() => {
                    if (!isProcessingUnwillingness) {
                        setUnwillingnessModalOpen(false);
                        setSelectedUnwillingnessRecord(null);
                        setUnwillingnessApproverRemarks("");
                    }
                }}
                footer={null}
                centered
                closeIcon={<i className="ti ti-x text-dark" style={{ fontSize: '20px' }}></i>}
                width={720}
                zIndex={9999}
                bodyStyle={{ padding: '20px 28px' }}
            >
                {selectedUnwillingnessRecord && (
                    <div className="mt-3">
                        {/* Employee Card */}
                        <div className="p-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-3" style={{ backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                            <div className="d-flex align-items-center gap-3">
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-danger"
                                    style={{ width: '46px', height: '46px', backgroundColor: '#FEE2E2', border: '1px solid #FECACA', fontSize: '18px' }}
                                >
                                    {(selectedUnwillingnessRecord.employeeName || "E").charAt(0)}
                                </div>
                                <div>
                                    <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>
                                        {selectedUnwillingnessRecord.employeeName || "Employee"}
                                    </h6>
                                    <div className="text-muted small mt-0.5">
                                        Emp ID: <strong className="text-dark">{selectedUnwillingnessRecord.employeeNumber || selectedUnwillingnessRecord.empId || "N/A"}</strong>
                                        {selectedUnwillingnessRecord.designation ? ` · ${selectedUnwillingnessRecord.designation}` : ""}
                                        {selectedUnwillingnessRecord.department ? ` (${selectedUnwillingnessRecord.department})` : ""}
                                    </div>
                                </div>
                            </div>
                            <div className="text-end d-flex flex-column align-items-end gap-1">
                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1" style={{ fontSize: '12px' }}>
                                    Ref: {selectedUnwillingnessRecord.unwillingnessReferenceNumber || selectedUnwillingnessRecord.referenceNumber || selectedUnwillingnessRecord.willingnessReferenceNumber || "-"}
                                </span>
                                <div className="d-flex align-items-center gap-1.5 mt-0.5">
                                    <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-0.5 fw-semibold" style={{ fontSize: '11px' }}>
                                        Willingness: {selectedUnwillingnessRecord.willingnessStatus || "APPROVED"}
                                    </span>
                                    <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle px-2 py-0.5 fw-semibold" style={{ fontSize: '11px' }}>
                                        Unwillingness: {selectedUnwillingnessRecord.unwillingnessStatus || selectedUnwillingnessRecord.unwillingnessDetails?.status || selectedUnwillingnessRecord.status || "PENDING"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Location & Offered Flats Section */}
                        <div className="p-3 mb-3 border rounded-3 bg-white shadow-none">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="text-secondary small fw-semibold">
                                    Offered Flats in Location: <strong className="text-dark fs-6">{selectedUnwillingnessRecord.locationName || selectedUnwillingnessRecord.locationPreference || "N/A"}</strong>
                                </span>
                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1">
                                    Location ID: {selectedUnwillingnessRecord.locationId || 1}
                                </span>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                {(selectedUnwillingnessRecord.flats && selectedUnwillingnessRecord.flats.length > 0
                                    ? selectedUnwillingnessRecord.flats
                                    : [{ flatNo: getFormattedFlatNo(selectedUnwillingnessRecord), quarterType: selectedUnwillingnessRecord.quarterType }]
                                ).map((f: any, idx: number) => {
                                    const raw = f.flatNo || (f.item ? getFormattedFlatNo(f.item) : "-");
                                    const cleanFlat = raw.replace(/^Flat\s+/i, "");
                                    return (
                                        <div key={idx} className="p-2 border rounded bg-light d-flex align-items-center gap-2">
                                            {cleanFlat && cleanFlat !== "-" && cleanFlat !== "N/A" ? (
                                                <span className="badge bg-white text-dark border px-2.5 py-1" style={{ fontSize: '13px', fontWeight: 700 }}>
                                                    Flat {cleanFlat}
                                                </span>
                                            ) : null}
                                            {f.quarterType && (
                                                <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: '11px' }}>
                                                    Type {f.quarterType}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Willingness Details Grid */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="p-3 border rounded-3 bg-white h-100 shadow-none">
                                    <div className="text-secondary small mb-1 fw-semibold">Allotment Location</div>
                                    <div className="d-flex align-items-center gap-2 text-dark mt-1" style={{ fontSize: '15px', fontWeight: 600 }}>
                                        <i className="ti ti-map-pin text-danger" style={{ fontSize: '18px' }}></i>
                                        <span>{selectedUnwillingnessRecord.locationName || selectedUnwillingnessRecord.locationPreference || selectedUnwillingnessRecord.location || "N/A"}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="p-3 border rounded-3 bg-white h-100 shadow-none">
                                    <div className="text-secondary small mb-1 fw-semibold">Registration Ref / Turn</div>
                                    <div className="d-flex align-items-center justify-content-between mt-1">
                                        <span className="text-dark" style={{ fontSize: '16px', fontWeight: 700 }}>
                                            {selectedUnwillingnessRecord.registrationReferenceNumber || selectedUnwillingnessRecord.registrationRef || "-"}
                                        </span>
                                        {selectedUnwillingnessRecord.priorityNo != null && (
                                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '13px', fontWeight: 700 }}>
                                                Priority #{selectedUnwillingnessRecord.priorityNo}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="p-3 border rounded-3 bg-white h-100 shadow-none">
                                    <div className="text-secondary small mb-1 fw-semibold">Willingness Status</div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                                        <span className={`badge ${
                                            (selectedUnwillingnessRecord.willingnessStatus || "APPROVED").toUpperCase() === "APPROVED" 
                                                ? "bg-success text-white" 
                                                : "bg-primary text-white"
                                        } px-2.5 py-1`} style={{ fontSize: '13px', fontWeight: 700 }}>
                                            {selectedUnwillingnessRecord.willingnessStatus || "APPROVED"}
                                        </span>
                                        <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle px-2.5 py-1" style={{ fontSize: '13px', fontWeight: 600 }}>
                                            Unwillingness: {selectedUnwillingnessRecord.unwillingnessStatus || selectedUnwillingnessRecord.unwillingnessDetails?.status || selectedUnwillingnessRecord.status || "PENDING"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="p-3 border rounded-3 bg-white h-100 shadow-none">
                                    <div className="text-secondary small mb-1 fw-semibold">Request / Declaration Date</div>
                                    <div className="text-dark mt-1" style={{ fontSize: '15px', fontWeight: 600 }}>
                                        <i className="ti ti-calendar me-1.5 text-muted" style={{ fontSize: '16px' }}></i>
                                        {formatDateTime(selectedUnwillingnessRecord.unwillingnessDate || selectedUnwillingnessRecord.debarmentDate || selectedUnwillingnessRecord.requestDate || selectedUnwillingnessRecord.createdAt || selectedUnwillingnessRecord.date)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Employee Reason for Unwillingness */}
                        <div className="p-3 mb-3 rounded-3 bg-danger-subtle border border-danger-subtle">
                            <div className="d-flex align-items-start gap-2">
                                <i className="ti ti-message-2 text-danger fs-4 mt-0.5"></i>
                                <div>
                                    <strong className="text-dark d-block small mb-1">Employee Reason / Remarks for Unwillingness:</strong>
                                    <p className="mb-0 small text-danger-emphasis fw-medium" style={{ lineHeight: '1.5' }}>
                                        {selectedUnwillingnessRecord.unwillingnessRemarks || selectedUnwillingnessRecord.userRemarks || selectedUnwillingnessRecord.remarks || selectedUnwillingnessRecord.reason || selectedUnwillingnessRecord.unwillingnessDetails?.remarks || "No reason provided by applicant."}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {(() => {
                            const st = String(selectedUnwillingnessRecord.unwillingnessStatus || selectedUnwillingnessRecord.unwillingnessDetails?.status || selectedUnwillingnessRecord.status || "").toUpperCase().trim();
                            const isAlreadyProcessed = Boolean(st && !st.includes("PENDING") && st !== "SUBMITTED" && st !== "REQUESTED" && st !== "OPEN" && st !== "INITIATED");

                            return (
                                <>
                                    {/* Approver Remarks Input */}
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-dark mb-1">
                                            Approver Remarks <span className="text-muted fw-normal">(Optional for approve, recommended for reject)</span>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows={2}
                                            placeholder={isAlreadyProcessed ? "Request has already been processed." : "Enter remarks or justification for your decision..."}
                                            value={unwillingnessApproverRemarks}
                                            onChange={(e) => setUnwillingnessApproverRemarks(e.target.value)}
                                            disabled={isProcessingUnwillingness || isAlreadyProcessed}
                                            maxLength={300}
                                        />
                                    </div>

                                    {/* Footer Action Buttons */}
                                    <div className="d-flex align-items-center justify-content-between pt-3 border-top gap-2">
                                        <button
                                            className="btn btn-light px-3"
                                            onClick={() => {
                                                setUnwillingnessModalOpen(false);
                                                setSelectedUnwillingnessRecord(null);
                                                setUnwillingnessApproverRemarks("");
                                            }}
                                            disabled={isProcessingUnwillingness}
                                        >
                                            Close
                                        </button>

                                        <div className="d-flex align-items-center gap-2">
                                            {isAlreadyProcessed && (
                                                <span className="badge bg-secondary-subtle text-secondary border px-3 py-2 me-2">
                                                    Status: {st}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger d-flex align-items-center gap-1.5 px-3 fw-semibold shadow-xs"
                                                onClick={() => handleProcessUnwillingness("REJECT")}
                                                disabled={isProcessingUnwillingness || isAlreadyProcessed}
                                            >
                                                <i className="ti ti-x fs-5"></i>
                                                <span>Reject Unwillingness</span>
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-success d-flex align-items-center gap-1.5 px-4 fw-semibold shadow-xs"
                                                onClick={() => handleProcessUnwillingness("APPROVE")}
                                                disabled={isProcessingUnwillingness || isAlreadyProcessed}
                                            >
                                                {isProcessingUnwillingness ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ti ti-check fs-5"></i>
                                                        <span>Approve Unwillingness</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AdminRequests;
